/**
 * Community Messaging — TanStack Query Hooks
 *
 * React Query hooks for conversations, messages, reactions, and read state.
 * All types flow from Drizzle → Zod → community.ts schemas (Directive D3).
 *
 * @see BUILD_PLAN_INDEX.md — D3 (schema-driven types everywhere)
 * @see ERRATA §4.4 — onSettled invalidates conversations only, NOT messages
 * @see ERRATA §4.2 — client_temp_id for optimistic reconciliation
 * @see ERRATA §6.4 — canonical stale/GC times
 * @see ERRATA §8.2 — useTogglePin and useToggleReaction defined in Phase 2
 */

import {
  useQuery,
  useQueries,
  useMutation,
  useInfiniteQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { api } from './client';
import { useCurrentUser } from './hooks';
// Cycle note: api/asset-share.ts imports `useSendMessage` from this module, so
// this is a circular import. It is safe — both helpers are hoisted function
// declarations, and they are only ever called from inside a mutation
// callback, long after both modules (and the prefix constant) evaluate.
import { getSharedAssetId, isLegacyAssetShareContent } from './asset-share';
import {
  fetchConversations,
  fetchConversation,
  fetchMessages,
  fetchSenderProfiles,
  type SenderProfile,
  fetchCommunityMemberMap,
  type MemberProfile,
  fetchIntroduciblePairs,
  fetchTierChatLabels,
  setTierChatTitle,
  type TierChatLabel,
  sendMessage,
  editMessage,
  deleteMessage,
  moderateDeleteMessage,
  addReaction,
  removeReaction,
  markAsRead,
  createDMConversation,
  getOrCreateTierConversation,
  pinConversation,
  muteConversation,
  archiveConversation,
  updateReadReceipts,
  updateOnlineVisibility,
  leaveConversation,
  buildOptimisticMessage,
  uuidv7,
  type ConversationFilter,
  type ConversationListItem,
  type ConversationDetail,
  type MessageWithContext,
  type MessagePage,
  type SendMessagePayload,
  type UnreadCount,
  type DMResult,
  type PinResult,
  getAttachmentSignedUrl,
  searchMessages,
  type SearchMessageResult,
  fetchDirectoryMembers,
  fetchDirectoryExperts,
  fetchDirectoryMapPins,
  fetchDirectoryMember,
  fetchMemberContact,
  findOrCreateDm,
  fetchLeads,
  updateLeadStatus,
  createLead,
  type CreateLeadInput,
  fetchCommunityChannels,
  type DirectoryFilters,
  type DirectoryMember,
  type MapPin,
  type LeadRecord,
  type CommunityChannelItem,
  searchUsersForChat,
  fetchMembersForNewDm,
  type NewDmMember,
  blockUser,
  unblockUser,
  isUserBlocked,
  reportContent,
  type ReportContentInput,
  type ReportReceipt,
  createGroupConversation,
  addGroupMembers,
  removeGroupMember,
  setGroupMemberRole,
  updateGroup,
  leaveGroup,
  removeGroupFromList,
  type GroupDetailsInput,
  fetchOrgRoster,
  fetchMyOrgMembership,
  type MyOrgMembership,
  type OrgMember,
  fetchUserTier,
  type UserTierInfo,
  fetchParticipantRevenueMap,
  fetchCommunityChannelsWithTiers,
  // Feed — Posts, Reactions, Comments
  fetchPosts,
  fetchPost,
  createPost,
  deletePost,
  deleteComment,
  updatePost,
  updateSystemPost,
  fetchMyChatAdminScopes,
  type UpdatedPost,
  togglePostReaction,
  fetchPostComments,
  fetchPostReactors,
  fetchMessageReactors,
  type PostReactor,
  type Reactor,
  addPostComment,
  fetchCommunityTiersForHub,
  fetchTierIdentities,
  setTierIdentity,
  type TierIdentityRow,
  getMyTopTierCommunity,
  autoJoinTopTierAndGlobals,
  uploadPostImage,
  uploadPostAttachment,
  enqueueUserVideoTranscode,
  // Feed — Stories
  fetchStories,
  createStory,
  deleteStory,
  recordStoryView,
  fetchStoryViewers,
  uploadStoryImage,
  fetchStoryReactions,
  toggleStoryReaction,
  type StoryReactionsByStory,
  type FeedPost,
  type FeedPostPage,
  type PostReactionCount,
  type PostCommentWithAuthor,
  type CommunityHubCard,
  type StoryGroup,
  type StoryItem,
  type StoryViewer,
  type CreateStoryInput,
  type TierStoryGroup,
  // Community Engagement / Gamification
  fetchCommunityLeaderboard,
  fetchMyCommunityStats,
  fetchHasCommunityActivity,
  fetchCommunityBadges,
  fetchCommunityPerks,
  purchaseCommunityPerk,
  // Channel Settings + Members
  fetchChannelSettings,
  updateChannelSettings,
  fetchChannelMembers,
  searchAddableCommunityUsers,
  searchCommunityUsers,
  addChannelMembers,
  removeChannelMember,
  updateChannelMemberRole,
  type ChannelMemberRole,
  type UpdateChannelSettingsPatch,
} from './community';
import {
  addCommunityMembers,
  archiveCustomCommunity,
  createCustomCommunity,
  fetchCommunityMembers,
  fetchCommunityTierGrants,
  fetchCustomCommunities,
  fetchCustomCommunitiesForHub,
  grantCommunityTierAccess,
  removeCommunityMember,
  revokeCommunityTierAccess,
  unarchiveCustomCommunity,
  updateCommunityMemberRole,
  updateCustomCommunity,
  type CommunityMemberRow,
  type CommunityTierGrant,
  type CustomCommunityCard,
  type CustomCommunityCreateInput,
  type CustomCommunityRow,
  type CustomCommunityUpdateInput,
  type HubCard,
} from './community';
import type { Mention, PostImage, PostAttachment, PostLocation } from '@hermes/db/schema';
import type { UserBlock } from '@hermes/db/schema';
import type { PodNumber } from '@/lib/pods';
import { revenueRangeFromCompanySize } from '@/lib/pods';
import { getCachedFileUrl, setCachedFileUrl } from '@/lib/file-cache';

// ===========================================
// Query Keys — community namespace
// ERRATA §6.4: canonical stale/GC times
// ===========================================

export const communityKeys = {
  // When called WITHOUT filters, returns the 2-element prefix
  // ['community', 'conversations'] so invalidateQueries() partial-matches
  // every filtered list variant. The live list query is always created WITH
  // a filter object (Community.tsx passes `apiFilter`, never undefined), so a
  // 3-element invalidation key like [...,'conversations', undefined] would
  // FAIL TanStack's partialMatchKey at index 2 (typeof {} !== typeof undefined)
  // and never refetch — e.g. "Delete Conversation" wrote left_at but the chat
  // never left the sidebar. Passing a filter still returns the 3-element key
  // used as the actual query key.
  conversations: (filters?: ConversationFilter) =>
    (filters
      ? (['community', 'conversations', filters] as const)
      : (['community', 'conversations'] as const)),
  conversation: (id: string) =>
    ['community', 'conversation', id] as const,
  messages: (conversationId: string) =>
    ['community', 'messages', conversationId] as const,
  memberMap: ['community', 'memberMap'] as const,
  introduciblePairs: ['community', 'introduciblePairs'] as const,
  tierChatLabels: ['community', 'tierChatLabels'] as const,
  channelConversation: (channelId: string) =>
    ['community', 'channelConversation', channelId] as const,
  // Phase 6 — Directory & Leads
  directory: (filters?: DirectoryFilters) =>
    ['community', 'directory', filters] as const,
  directoryMapPins: ['community', 'directoryMapPins'] as const,
  directoryExperts: (filters?: DirectoryFilters) =>
    ['community', 'directoryExperts', filters] as const,
  directoryMember: (userId: string) =>
    ['community', 'directoryMember', userId] as const,
  leads: (role: string, status?: string) =>
    ['community', 'leads', role, status] as const,
  channels: (tierId?: string) =>
    ['community', 'channels', tierId] as const,
  // Phase 7 — Search
  searchMessages: (query: string, conversationId?: string) =>
    ['community', 'searchMessages', query, conversationId] as const,
  // Phase 8a — Blocks
  isBlocked: (userId: string) =>
    ['community', 'isBlocked', userId] as const,
  // Teams & Community
  orgMembers: ['community', 'orgMembers'] as const,
  // Canonical "my org membership" — org id + revenue/size in one row. Backs
  // useMyOrgMembership, useUserOrganizationId, and useUserOrgRevenueRange.
  myOrgMembership: ['community', 'myOrgMembership'] as const,
  userTier: ['community', 'userTier'] as const,
  channelsWithTiers: ['community', 'channelsWithTiers'] as const,
  // Feed — Posts, Reactions, Comments
  // The `pods` key is a sorted, comma-joined list so React Query de-dupes
  // equivalent selections regardless of toggle order.
  posts: (tierId: string, channelId?: string, pods?: PodNumber[]) =>
    ['community', 'posts', tierId, channelId ?? 'all', pods && pods.length > 0 ? [...pods].sort().join(',') : 'all'] as const,
  post: (postId: string) =>
    ['community', 'post', postId] as const,
  postComments: (postId: string) =>
    ['community', 'postComments', postId] as const,
  hubCards: ['community', 'hubCards'] as const,
  // Stories
  stories: (tierId: string) =>
    ['community', 'stories', tierId] as const,
  storyViewers: (storyId: string) =>
    ['community', 'storyViewers', storyId] as const,
  storyReactions: (tierId: string) =>
    ['community', 'storyReactions', tierId] as const,
  // Pods — revenue-based filtering
  participantRevenueMap: ['community', 'participantRevenueMap'] as const,
  // Community Engagement / Gamification
  leaderboard: (timeframe: string) =>
    ['community', 'leaderboard', timeframe] as const,
  myStats: () =>
    ['community', 'myStats'] as const,
  hasActivity: () =>
    ['community', 'hasActivity'] as const,
  badges: () =>
    ['community', 'badges'] as const,
  perks: () =>
    ['community', 'perks'] as const,
} as const;

// Canonical stale/GC times (ERRATA §6.4)
const STALE_TIMES = {
  messages: Infinity, // realtime-driven in Phase 3
  conversations: 30_000, // 30s
} as const;

const GC_TIMES = {
  messages: 24 * 60 * 60_000, // 24h — cache survives navigation within a session
  conversations: 5 * 60_000, // 5 min
} as const;

/**
 * Conversation ids in the "warm" working set — the top-N most-recent (set by
 * `useWarmCommunity`) plus the active conversation (added by
 * `setActiveConversationId`). The IndexedDB persister (main.tsx) only serializes
 * messages for these ids, so the persist cost stays FLAT over a session instead
 * of growing with every conversation ever opened — the root of the interaction
 * decay. On refresh these restore instantly; everything else reconciles live.
 */
export const warmConversationIds = new Set<string>();
/** Cap so a pathological list can't grow the persisted set unbounded. */
export const WARM_CONVERSATION_LIMIT = 10;

/**
 * Locally adjust unread state WITHOUT refetching the heavy
 * `get_conversations_for_user` RPC. Patches every ['community','conversations',*]
 * cache (the canonical list plus any live search variants); unread counts are a
 * client-side `select` over the same canonical list (useUnreadCounts), so the
 * top "Chat" pill and browser-tab title update from this single write too.
 *
 * Replaces the per-message RPC invalidation that caused the 2026-06-15 stampede
 * (one message fanned out to 2×N heavy-RPC refetches → connection-pool exhaustion).
 * Server-authoritative truth is reconciled by the throttled visibility-gated
 * reconcile (reconcileOnReconnect) and the cross-tab UNREAD_CHANGED event.
 */
export function applyUnread(
  queryClient: ReturnType<typeof useQueryClient>,
  conversationId: string,
  mode: { kind: 'increment' } | { kind: 'set'; count: number },
): void {
  queryClient.setQueriesData<ConversationListItem[]>(
    { queryKey: ['community', 'conversations'] },
    (old) => {
      if (!old) return old;
      return old.map((c) => {
        if (c.id !== conversationId) return c;
        const next = mode.kind === 'set' ? mode.count : (c.unread_count ?? 0) + 1;
        return { ...c, unread_count: Math.max(0, next) };
      });
    },
  );
}

// ===========================================
// Conversation Hooks
// ===========================================

/**
 * True when the filter needs a server round-trip. Only text search does —
 * `p_search` also matches participant names beyond the fetched window. A bare
 * type filter is a plain equality on `c.type` in the RPC, so it is derived
 * client-side from the canonical list instead of refetching.
 */
function needsServerFetch(filters?: ConversationFilter): boolean {
  return !!filters?.search;
}

/**
 * Fetch conversation list for sidebar.
 * Grouped by type on the consumer side.
 *
 * ONE canonical query (['community','conversations']) serves all type-filter
 * tabs and unread counts; tab variants are a client-side `select`. This
 * replaced the per-filter query keys that fired the heavy
 * `get_conversations_for_user` RPC 10×+ per Community visit (2026-07-02 audit).
 * Only a text search issues its own server query.
 */
export function useConversations(filters?: ConversationFilter) {
  const { data: user } = useCurrentUser();
  const serverFetch = needsServerFetch(filters);
  return useQuery<ConversationListItem[]>({
    queryKey: serverFetch
      ? communityKeys.conversations(filters)
      : communityKeys.conversations(),
    queryFn: () => fetchConversations(user!.id, serverFetch ? filters : undefined),
    select: serverFetch
      ? undefined
      : (all) => (filters?.type ? all.filter((c) => c.type === filters.type) : all),
    enabled: !!user,
    staleTime: STALE_TIMES.conversations,
    gcTime: GC_TIMES.conversations,
    refetchOnWindowFocus: true,
    placeholderData: (prev) => prev, // show previous data while a search loads
  });
}

/**
 * Prefetch the canonical conversation list so tab switching is instant.
 * Call once when the Chat tab mounts. All filter tabs derive from this single
 * cache entry via `select` in useConversations — one RPC warms every tab
 * (previously 4 per-filter queries = 4 heavy RPC calls on mount).
 */

/**
 * Prewarmed id → {full_name, avatar_url} map for the whole community the user
 * can see. Lets reactor / mention / participant tooltips resolve names+avatars
 * INSTANTLY from cache instead of a per-hover fetch. Loaded once by
 * `useWarmCommunity` and reused everywhere (supersedes the per-conversation
 * `sender-profiles` proliferation). Falls back to a targeted fetch only for ids
 * not present (e.g. a brand-new member mid-session).
 */
export function useCommunityMemberMap() {
  const { data: user } = useCurrentUser();
  return useQuery<Map<string, MemberProfile>>({
    queryKey: communityKeys.memberMap,
    queryFn: async () => {
      const list = await fetchCommunityMemberMap();
      return new Map(list.map((m) => [m.user_id, m]));
    },
    enabled: !!user,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });
}

/**
 * Prewarmed map of memberId → scope_key[] for members the current user may
 * introduce (mig 370 `introducible_pairs`). Loaded once by `useWarmCommunity`
 * and read client-side for BOTH the "Introduce" button visibility (`.has(id)`)
 * and the picker candidate list (members sharing ≥1 scope_key with the card
 * member) — so browsing profiles and typing in the picker make ZERO server
 * calls. Empty for global admins (they use the global member search).
 */
export function useIntroduciblePairs() {
  const { data: user } = useCurrentUser();
  return useQuery<Map<string, string[]>>({
    queryKey: communityKeys.introduciblePairs,
    queryFn: async () => {
      const pairs = await fetchIntroduciblePairs();
      const map = new Map<string, string[]>();
      for (const { member_id, scope_key } of pairs) {
        const scopes = map.get(member_id);
        if (scopes) scopes.push(scope_key);
        else map.set(member_id, [scope_key]);
      }
      return map;
    },
    enabled: !!user,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });
}

/** super_admin: all tiers + their chat title, for the admin Tier Settings panel. */
export function useTierChatLabels(enabled = true) {
  const { data: user } = useCurrentUser();
  return useQuery<TierChatLabel[]>({
    queryKey: communityKeys.tierChatLabels,
    queryFn: fetchTierChatLabels,
    enabled: !!user && enabled,
    staleTime: 60_000,
  });
}

/** super_admin: set a tier's chat title (display alias). Invalidates the caches
 *  that render tier/chat names (tier labels, hub cards, conversations) so every
 *  surface + other tabs refresh (persisted RQ would otherwise show the old name). */
export function useSetTierChatTitle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tierId, title }: { tierId: string; title: string | null }) =>
      setTierChatTitle(tierId, title),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: communityKeys.tierChatLabels });
      void queryClient.invalidateQueries({ queryKey: communityKeys.hubCards });
      void queryClient.invalidateQueries({ queryKey: ['community', 'conversations'] });
    },
  });
}

export function usePrefetchChatFilters() {
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser();

  useEffect(() => {
    if (!user) return;
    queryClient.prefetchQuery({
      queryKey: communityKeys.conversations(),
      queryFn: () => fetchConversations(user.id),
      staleTime: STALE_TIMES.conversations,
    });
  }, [user, queryClient]);
}

/**
 * Warm the community on load so navigation is instant. Call once at the
 * Community root. Prewarms:
 *   - the top 10 conversations' first message page (by last_message_at) → no
 *     spinner when switching between them;
 *   - the user's community feed(s) → the Feed tab renders immediately;
 *   - the member id→name/avatar map → reaction/mention/participant tooltips are
 *     instant.
 * The top-10 set is also intended to be the persistence bound (see main.tsx).
 */
export function useWarmCommunity() {
  const queryClient = useQueryClient();
  const { data: conversations } = useConversations();
  const { data: hubCards } = useCommunityTiersForHub();
  // Trigger the member map fetch (its data is consumed by tooltip components).
  useCommunityMemberMap();
  // Prewarm the "introducible" scope map so the Introduce button + picker are
  // resolved from cache (no per-profile / per-keystroke calls). Empty for global
  // admins, so this is a near-free extra prewarm.
  useIntroduciblePairs();

  // Top 10 conversations' first page of messages.
  useEffect(() => {
    if (!conversations || conversations.length === 0) return;
    const top = [...conversations]
      .sort((a, b) => (b.last_message_at ?? '').localeCompare(a.last_message_at ?? ''))
      .slice(0, WARM_CONVERSATION_LIMIT);
    // Rebuild the persistence bound to exactly this warm set.
    warmConversationIds.clear();
    for (const c of top) warmConversationIds.add(c.id);
    for (const c of top) {
      void queryClient.prefetchInfiniteQuery({
        queryKey: communityKeys.messages(c.id),
        queryFn: ({ pageParam }) => fetchMessages(c.id, pageParam as string | undefined, 30),
        initialPageParam: undefined as string | undefined,
        staleTime: STALE_TIMES.messages,
      });
    }
  }, [conversations, queryClient]);

  // The user's community feed(s) — default view (no channel/pod filter).
  useEffect(() => {
    if (!hubCards || hubCards.length === 0) return;
    for (const card of hubCards) {
      void queryClient.prefetchInfiniteQuery({
        queryKey: communityKeys.posts(card.id),
        queryFn: ({ pageParam }) => fetchPosts(card.id, pageParam as string | undefined, 15),
        initialPageParam: undefined as string | undefined,
        staleTime: 30_000,
      });
    }
  }, [hubCards, queryClient]);
}

/**
 * Fetch single conversation with full participant list.
 */
export function useConversation(id: string) {
  return useQuery<ConversationDetail>({
    queryKey: communityKeys.conversation(id),
    queryFn: () => fetchConversation(id),
    enabled: !!id,
    staleTime: STALE_TIMES.conversations,
    gcTime: GC_TIMES.conversations,
  });
}

/**
 * Infinite query for messages with cursor-based pagination.
 * staleTime = Infinity — realtime will handle updates in Phase 3.
 */
export function useMessages(conversationId: string) {
  return useInfiniteQuery<MessagePage>({
    queryKey: communityKeys.messages(conversationId),
    queryFn: ({ pageParam }) =>
      fetchMessages(conversationId, pageParam as string | undefined, 30),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!conversationId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(conversationId),
    staleTime: STALE_TIMES.messages,
    gcTime: GC_TIMES.messages,
  });
}

/**
 * Fresh sender profiles (name + avatar) keyed by user id, for overlaying onto
 * messages. Unlike the messages query (staleTime:Infinity + persisted), this
 * has a finite staleTime so a profile rename propagates into already-cached
 * conversations. Returns a Map for O(1) lookup by sender_id.
 */
export function useFreshSenderProfiles(userIds: string[]) {
  const ids = useMemo(() => Array.from(new Set(userIds)).sort(), [userIds]);
  const query = useQuery<Record<string, SenderProfile>>({
    queryKey: ['community', 'sender-profiles', ids],
    queryFn: () => fetchSenderProfiles(ids),
    enabled: ids.length > 0,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
  return useMemo(() => {
    const map = new Map<string, SenderProfile>();
    for (const [id, p] of Object.entries(query.data ?? {})) map.set(id, p);
    return map;
  }, [query.data]);
}

/**
 * Unread counts for all conversations.
 *
 * A client-side `select` over the SAME canonical conversations query as
 * useConversations — the RPC already returns unread_count per row, so this
 * costs ZERO extra network (previously it fired its own duplicate
 * `get_conversations_for_user` call, a steady contributor to the 2026-06-15
 * connection-pool exhaustion). NOT polled — between fetches the cache is kept
 * live locally by `applyUnread` from the realtime broadcast handlers and
 * `useMarkAsRead`. The throttled visibility-gated reconcile corrects any drift.
 */
export function useUnreadCounts() {
  const { data: user } = useCurrentUser();
  return useQuery<ConversationListItem[], Error, UnreadCount[]>({
    queryKey: communityKeys.conversations(),
    queryFn: () => fetchConversations(user!.id),
    select: (all) =>
      all
        .filter((c) => (c.unread_count ?? 0) > 0)
        .map((c) => ({
          conversation_id: c.id,
          count: c.unread_count ?? 0,
          type: c.type,
          muted: c.muted ?? false,
        })),
    enabled: !!user,
    staleTime: STALE_TIMES.conversations,
    gcTime: GC_TIMES.conversations,
    refetchOnWindowFocus: true,
  });
}

// ===========================================
// Message Mutation Hooks
// ===========================================

/** The sidebar line for an asset share that carries no typed text. */
export const ASSET_SHARE_PREVIEW = 'Shared an asset';

/** Sidebar previews are one line; the server trigger cuts at 100, this at 80. */
const PREVIEW_MAX_LENGTH = 80;

/**
 * The conversation-list preview line for a message.
 *
 * An asset share's content is the SENDER'S OWN WORDS and may be empty, and the
 * asset's title must never appear here: the row is visible to every
 * participant, including people who cannot open the asset (same rule as
 * `AssetShareCard` — api/asset-share.ts). So a text-less asset share reads
 * "Shared an asset" rather than leaving a blank line.
 *
 * SCOPE: this is the SENDER's optimistic patch only. Two server-side values
 * carry the same line for everyone else and both still need the same fallback:
 *   - `conversations.last_message_preview`, written by
 *     `fn_update_conversation_last_message` (hermes mig 127) as
 *     `LEFT(NEW.content, 100)` — what the list shows after any refetch;
 *   - the `conversation.updated` broadcast preview built in
 *     `fn_broadcast_message_events` (hermes mig 468), which already does
 *     exactly this for member intros (`metadata ? 'shared_member_name'`).
 * There is no metadata on the conversation row, so the frontend cannot derive
 * it for anyone but the sender.
 */
export function buildMessagePreview(payload: {
  content?: string | null;
  attachments?: unknown[] | null;
  messageType?: string | null;
  metadata?: unknown;
}): string {
  // A legacy share's generated "Shared an asset: <title>" line is not the
  // sender's words, and quoting it would leak the title: same rule as the
  // bubble and the server-side preview (message_preview_text, hermes mig 645).
  const trimmed = isLegacyAssetShareContent(payload.content, payload.metadata)
    ? ''
    : payload.content?.trim() ?? '';
  const source = trimmed
    || (getSharedAssetId(payload.metadata) ? ASSET_SHARE_PREVIEW : '')
    || (payload.attachments?.length ? `[${payload.messageType ?? 'attachment'}]` : '');
  return source.length > PREVIEW_MAX_LENGTH
    ? source.slice(0, PREVIEW_MAX_LENGTH) + '…'
    : source;
}

/**
 * Send a message with optimistic update.
 *
 * ERRATA §4.4: onSettled only invalidates conversations (sidebar preview).
 * ERRATA §4.2: client_temp_id for reconciliation.
 * onError: sets optimistic message to 'failed' status (no rollback).
 */
export function useSendMessage() {
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser();

  return useMutation<
    MessageWithContext,
    Error,
    { conversationId: string; payload: SendMessagePayload; replyTo?: { id: string; content: string; sender_name: string } | null },
    { messageId: string }
  >({
    // Generate the message id client-side (UUIDv7) and pass it through to
    // the server INSERT. Optimistic and confirmed rows now share the same
    // id, so id-DESC sort on the receiver matches the sender's typed
    // order — no temp-id reconciliation dance, no server-jitter race.
    mutationFn: ({ conversationId, payload }) =>
      sendMessage(conversationId, payload),

    onMutate: async ({ conversationId, payload, replyTo }) => {
      if (!user) return { messageId: '' };

      // Generate UUIDv7 here so optimistic + server INSERT share the id.
      const messageId = payload.id || uuidv7();
      const payloadWithId = { ...payload, id: messageId };

      await queryClient.cancelQueries({
        queryKey: communityKeys.messages(conversationId),
      });

      const optimistic = buildOptimisticMessage(
        conversationId,
        payloadWithId,
        { id: user.id, full_name: user.full_name || 'You', avatar_url: user.avatar_url },
        replyTo,
      );

      queryClient.setQueryData<InfiniteData<MessagePage>>(
        communityKeys.messages(conversationId),
        (old) => {
          if (!old) return old;
          const newPages = [...old.pages];
          newPages[0] = {
            ...newPages[0],
            messages: [optimistic, ...newPages[0].messages],
          };
          return { ...old, pages: newPages };
        },
      );

      // Optimistically bubble the sender's own conversation to the top of the
      // sidebar. The server-side broadcastConversationUpdate excludes the
      // sender, so without this the conversation only reorders when a peer
      // replies (or when onSettled's invalidation refetches). Server timestamp
      // reconciles on the eventual refetch.
      const preview = buildMessagePreview(payload);
      const nowIso = new Date().toISOString();
      queryClient.setQueriesData<ConversationListItem[]>(
        { queryKey: ['community', 'conversations'] },
        (old) => {
          if (!old) return old;
          return old.map((c) =>
            c.id === conversationId
              ? {
                  ...c,
                  last_message_at: nowIso,
                  last_message_preview: preview || c.last_message_preview,
                  last_message_sender_name: user.full_name || c.last_message_sender_name,
                }
              : c,
          );
        },
      );

      // IMPORTANT: mutationFn must receive the same id we used for optimistic.
      // We mutate the input payload here so sendMessage uses the right id.
      // (TanStack Mutation passes the original payload reference to mutationFn.)
      payload.id = messageId;

      return { messageId };
    },

    // ERRATA §4.4: do NOT roll back on error. Set _status: 'failed' instead.
    // Lookup is now by message id (same id as optimistic, no temp- prefix).
    onError: (_err, { conversationId }, context) => {
      if (!context?.messageId) return;
      queryClient.setQueryData<InfiniteData<MessagePage>>(
        communityKeys.messages(conversationId),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              messages: page.messages.map((m) =>
                m.id === context.messageId
                  ? { ...m, metadata: { ...m.metadata, _status: 'failed' } }
                  : m,
              ),
            })),
          };
        },
      );
    },

    // Reconcile optimistic row with real server response. Both share the
    // same id (we sent it client-side), so this is a metadata refresh —
    // strip the _optimistic flag, attach the server-resolved sender etc.
    // Supabase broadcast doesn't echo to the sender (self: false), so the
    // sender always reconciles locally from the mutation response.
    onSuccess: (data, { conversationId }, context) => {
      if (!context?.messageId) return;
      queryClient.setQueryData<InfiniteData<MessagePage>>(
        communityKeys.messages(conversationId),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              messages: page.messages.map((m) =>
                m.id === context.messageId ? { ...data } : m,
              ),
            })),
          };
        },
      );
    },

    // ERRATA §4.4: only invalidate conversations list (sidebar), NOT messages
    onSettled: (_data, _err, { conversationId }) => {
      queryClient.invalidateQueries({
        queryKey: communityKeys.conversations(),
      });
    },
  });
}

/**
 * Edit a message. Invalidates messages on settle.
 */
export function useEditMessage(conversationId: string) {
  const queryClient = useQueryClient();

  return useMutation<
    MessageWithContext,
    Error,
    { messageId: string; content: string }
  >({
    mutationFn: ({ messageId, content }) => editMessage(messageId, content),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: communityKeys.messages(conversationId),
      });
    },
  });
}

/**
 * Soft-delete a message with optimistic update.
 * ERRATA §5.7: content cleared alongside deleted_at.
 */
export function useDeleteMessage(conversationId: string) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (messageId: string) => deleteMessage(messageId),
    onMutate: async (messageId) => {
      await queryClient.cancelQueries({
        queryKey: communityKeys.messages(conversationId),
      });
      // Optimistic: mark as deleted in cache (ERRATA §4.3: InfiniteData)
      queryClient.setQueryData<InfiniteData<MessagePage>>(
        communityKeys.messages(conversationId),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              messages: page.messages.map((m) =>
                m.id === messageId
                  ? { ...m, deleted_at: new Date().toISOString(), content: '' }
                  : m,
              ),
            })),
          };
        },
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: communityKeys.messages(conversationId),
      });
      queryClient.invalidateQueries({
        queryKey: communityKeys.conversations(),
      });
    },
  });
}

/**
 * Moderator delete of another member's message (channel admin/owner or super_admin).
 * Same optimistic + settle behaviour as useDeleteMessage; goes via the
 * moderate_delete_message RPC. Authorisation is enforced server-side.
 */
export function useModerateDeleteMessage(conversationId: string) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (messageId: string) => moderateDeleteMessage(messageId),
    onMutate: async (messageId) => {
      await queryClient.cancelQueries({
        queryKey: communityKeys.messages(conversationId),
      });
      queryClient.setQueryData<InfiniteData<MessagePage>>(
        communityKeys.messages(conversationId),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              messages: page.messages.map((m) =>
                m.id === messageId
                  ? { ...m, deleted_at: new Date().toISOString(), content: '' }
                  : m,
              ),
            })),
          };
        },
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: communityKeys.messages(conversationId),
      });
      queryClient.invalidateQueries({
        queryKey: communityKeys.conversations(),
      });
    },
  });
}

// ===========================================
// Reaction Hooks — ERRATA §8.2: defined in Phase 2
// ===========================================

/**
 * Add a reaction with optimistic update.
 */
export function useAddReaction(conversationId: string) {
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser();

  return useMutation<void, Error, { messageId: string; emoji: string }>({
    mutationFn: ({ messageId, emoji }) => addReaction(messageId, emoji, conversationId),
    onMutate: async ({ messageId, emoji }) => {
      await queryClient.cancelQueries({
        queryKey: communityKeys.messages(conversationId),
      });
      queryClient.setQueryData<InfiniteData<MessagePage>>(
        communityKeys.messages(conversationId),
        (old) => {
          if (!old) return old;
          return updateReactionInCache(old, messageId, emoji, 'add', user?.id);
        },
      );
    },
    onSettled: (_data, _err, { messageId }) => {
      queryClient.invalidateQueries({
        queryKey: communityKeys.messages(conversationId),
      });
      // Drop the cached reactors list so the next hover refetches.
      queryClient.invalidateQueries({
        queryKey: ['community', 'message', messageId, 'reactors'],
      });
    },
  });
}

/**
 * Remove a reaction with optimistic update.
 */
export function useRemoveReaction(conversationId: string) {
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser();

  return useMutation<void, Error, { messageId: string; emoji: string }>({
    mutationFn: ({ messageId, emoji }) => removeReaction(messageId, emoji, conversationId),
    onMutate: async ({ messageId, emoji }) => {
      await queryClient.cancelQueries({
        queryKey: communityKeys.messages(conversationId),
      });
      queryClient.setQueryData<InfiniteData<MessagePage>>(
        communityKeys.messages(conversationId),
        (old) => {
          if (!old) return old;
          return updateReactionInCache(old, messageId, emoji, 'remove', user?.id);
        },
      );
    },
    onSettled: (_data, _err, { messageId }) => {
      queryClient.invalidateQueries({
        queryKey: communityKeys.messages(conversationId),
      });
      queryClient.invalidateQueries({
        queryKey: ['community', 'message', messageId, 'reactors'],
      });
    },
  });
}

/**
 * Toggle reaction — convenience wrapper (ERRATA §8.2).
 * Adds if not reacted, removes if already reacted.
 */
export function useToggleReaction(conversationId: string) {
  const add = useAddReaction(conversationId);
  const remove = useRemoveReaction(conversationId);

  return {
    mutate: ({ messageId, emoji, currentlyReacted }: {
      messageId: string;
      emoji: string;
      currentlyReacted: boolean;
    }) => {
      if (currentlyReacted) {
        remove.mutate({ messageId, emoji });
      } else {
        add.mutate({ messageId, emoji });
      }
    },
    isPending: add.isPending || remove.isPending,
  };
}

// ===========================================
// Read State Hooks
// ===========================================

/**
 * Mark conversation as read.
 * Consumer should debounce (500ms) — ERRATA §2.4.
 */
export function useMarkAsRead() {
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser();

  return useMutation<
    void,
    Error,
    { conversationId: string; messageId: string }
  >({
    // Pass the cached user id so markAsRead skips a per-call network getUser()
    // (2026-06-15: auth shares the DB pool; trimming /auth/v1/user load).
    mutationFn: ({ conversationId, messageId }) =>
      markAsRead(conversationId, messageId, user?.id),
    onSuccess: (_data, { conversationId }) => {
      // Clear unread in BOTH caches locally — no get_conversations_for_user refetch
      // (the per-mark-as-read invalidation was a stampede contributor on 2026-06-15).
      applyUnread(queryClient, conversationId, { kind: 'set', count: 0 });
    },
  });
}

/**
 * Auto-mark a conversation read as new confirmed messages arrive — debounced so a
 * burst collapses to a SINGLE mark_as_read RPC + READ_UPDATED broadcast instead of
 * one per message (2026-06-15 stampede contributor — ERRATA §2.4). The pending mark
 * is flushed on conversation switch / unmount so the read pointer is never lost.
 *
 * @param latestConfirmedMessageId newest server-confirmed (non-optimistic) message
 *   id, or null when there's nothing markable.
 * @param enabled gate (e.g. GroupDetailView only marks read on the active chat tab).
 */
export function useAutoMarkRead(
  conversationId: string,
  latestConfirmedMessageId: string | null,
  enabled: boolean = true,
): void {
  const { mutate } = useMarkAsRead();
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;
  const pendingRef = useRef<{ conversationId: string; messageId: string } | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const flush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
    const pending = pendingRef.current;
    if (pending) {
      pendingRef.current = null;
      mutateRef.current(pending);
    }
  }, []);

  // Only commit a mark-as-read while the tab is actually visible. A hidden tab
  // (backgrounded, phone locked, PWA in the app switcher) can still mount a chat
  // and run these effects, which would silently clear an unread the user never saw.
  const flushIfVisible = useCallback(() => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      // Keep pendingRef intact — the visibilitychange listener flushes on return.
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }
      return;
    }
    flush();
  }, [flush]);

  useEffect(() => {
    if (!enabled || !conversationId || !latestConfirmedMessageId) return;
    pendingRef.current = { conversationId, messageId: latestConfirmedMessageId };
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flushIfVisible, 1500);
  }, [conversationId, latestConfirmedMessageId, enabled, flushIfVisible]);

  // When the tab becomes visible again, commit anything that was held back while
  // it was hidden — the user is now genuinely looking at the conversation.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (!pendingRef.current) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flushIfVisible, 1500);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [flushIfVisible]);

  // Flush any pending mark on conversation switch / unmount (don't drop it),
  // but still respect visibility — a hidden tab must not commit on teardown.
  useEffect(() => {
    return () => {
      flushIfVisible();
    };
  }, [conversationId, flushIfVisible]);
}

// ===========================================
// Conversation CRUD Hooks
// ===========================================

/**
 * Create a DM conversation (or find existing) — ERRATA §8.3.
 */
export function useCreateDM() {
  const queryClient = useQueryClient();

  return useMutation<DMResult, Error, string>({
    mutationFn: (targetUserId) => createDMConversation(targetUserId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: communityKeys.conversations(),
      });
    },
  });
}

/**
 * Get or create the single chat conversation for a tier.
 * Chat is keyed on `conversations.community_tier_id`, not on community_channels.
 */
export function useTierConversation(tierId: string) {
  return useQuery<DMResult>({
    queryKey: ['community', 'tierConversation', tierId] as const,
    queryFn: () => getOrCreateTierConversation(tierId),
    enabled: !!tierId,
    staleTime: Infinity,
  });
}

/**
 * Toggle pin on a conversation — ERRATA §8.2: defined in Phase 2.
 * Optimistic update on conversations list.
 */
export function useTogglePin() {
  const queryClient = useQueryClient();

  return useMutation<PinResult, Error, string>({
    mutationFn: (conversationId: string) => pinConversation(conversationId),
    onMutate: async (conversationId) => {
      await queryClient.cancelQueries({
        queryKey: communityKeys.conversations(),
      });
      // Optimistic toggle across all conversation list caches
      queryClient.setQueriesData<ConversationListItem[]>(
        { queryKey: ['community', 'conversations'] },
        (old) => {
          if (!old) return old;
          return old.map((c) =>
            c.id === conversationId ? { ...c, pinned: !c.pinned } : c,
          );
        },
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: communityKeys.conversations(),
      });
    },
  });
}

/**
 * Mute/unmute a conversation.
 */
export function useMuteConversation() {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    { conversationId: string; until?: string | null }
  >({
    mutationFn: ({ conversationId, until }) =>
      muteConversation(conversationId, until),
    onSettled: (_data, _err, { conversationId }) => {
      queryClient.invalidateQueries({
        queryKey: communityKeys.conversations(),
      });
      queryClient.invalidateQueries({
        queryKey: communityKeys.conversation(conversationId),
      });
    },
  });
}

/**
 * Archive a conversation.
 */
export function useArchiveConversation() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (conversationId: string) => archiveConversation(conversationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: communityKeys.conversations(),
      });
    },
  });
}

/**
 * Toggle read receipts for a conversation (DB-backed).
 */
export function useToggleReadReceipts() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { conversationId: string; enabled: boolean }>({
    mutationFn: ({ conversationId, enabled }) => updateReadReceipts(conversationId, enabled),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: communityKeys.conversations() });
    },
  });
}

/**
 * Toggle online status visibility (DB-backed, global).
 */
export function useToggleOnlineVisibility() {
  return useMutation<void, Error, boolean>({
    mutationFn: (hidden: boolean) => updateOnlineVisibility(hidden),
  });
}

/**
 * Leave a conversation (set left_at).
 *
 * Optimistically drops the conversation from every cached list variant so the
 * sidebar updates instantly AND the open-pane auto-select (Community.tsx) can't
 * re-pick the just-left conversation from a stale, not-yet-refetched list.
 * onSettled still invalidates to reconcile with the server.
 */
export function useLeaveConversation() {
  const queryClient = useQueryClient();
  return useMutation<
    void,
    Error,
    string,
    { previous: Array<[QueryKey, ConversationListItem[] | undefined]> }
  >({
    mutationFn: (conversationId: string) => leaveConversation(conversationId),
    onMutate: async (conversationId) => {
      // Stop in-flight list refetches from clobbering the optimistic prune.
      await queryClient.cancelQueries({ queryKey: communityKeys.conversations() });
      const previous = queryClient.getQueriesData<ConversationListItem[]>({
        queryKey: communityKeys.conversations(),
      });
      for (const [key, list] of previous) {
        if (!list) continue;
        queryClient.setQueryData<ConversationListItem[]>(
          key,
          list.filter((c) => c.id !== conversationId),
        );
      }
      return { previous };
    },
    onError: (_err, _conversationId, context) => {
      // Roll back the optimistic prune on failure.
      context?.previous.forEach(([key, list]) => queryClient.setQueryData(key, list));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: communityKeys.conversations() });
    },
  });
}

// ===========================================
// Cache Helpers
// ===========================================

/**
 * Update reaction count in InfiniteData cache (ERRATA §4.3: paginated structure).
 */
function updateReactionInCache(
  data: InfiniteData<MessagePage>,
  messageId: string,
  emoji: string,
  action: 'add' | 'remove',
  currentUserId?: string,
): InfiniteData<MessagePage> {
  // Maintain user_ids alongside count so the reactor tooltip stays instant even
  // during the optimistic window (the user's own reaction shows immediately).
  const withUser = (ids: string[] = []) =>
    currentUserId && !ids.includes(currentUserId) ? [...ids, currentUserId] : ids;
  const withoutUser = (ids: string[] = []) =>
    currentUserId ? ids.filter((id) => id !== currentUserId) : ids;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      messages: page.messages.map((m) => {
        if (m.id !== messageId) return m;
        const existing = m.reactions.find((r) => r.emoji === emoji);
        let newReactions;
        if (action === 'add') {
          if (existing) {
            newReactions = m.reactions.map((r) =>
              r.emoji === emoji
                ? { ...r, count: r.count + 1, reacted_by_me: true, user_ids: withUser(r.user_ids) }
                : r,
            );
          } else {
            newReactions = [
              ...m.reactions,
              { emoji, count: 1, reacted_by_me: true, user_ids: currentUserId ? [currentUserId] : [] },
            ];
          }
        } else {
          if (existing && existing.count <= 1) {
            newReactions = m.reactions.filter((r) => r.emoji !== emoji);
          } else {
            newReactions = m.reactions.map((r) =>
              r.emoji === emoji
                ? { ...r, count: r.count - 1, reacted_by_me: false, user_ids: withoutUser(r.user_ids) }
                : r,
            );
          }
        }
        return { ...m, reactions: newReactions };
      }),
    })),
  };
}

// ===========================================
// Directory Hooks — Phase 6
// ERRATA §6.4: directory staleTime = 60s, gcTime = 5min
// ===========================================

export function useDirectoryMembers(filters: DirectoryFilters = {}) {
  return useQuery({
    queryKey: communityKeys.directory(filters),
    queryFn: () => fetchDirectoryMembers(filters),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
}

export function useDirectoryExperts(filters: DirectoryFilters = {}, enabled = true) {
  return useQuery({
    queryKey: communityKeys.directoryExperts(filters),
    queryFn: () => fetchDirectoryExperts(filters),
    enabled,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
}

// ===========================================
// Chat User Search — for New Chat member picker
// No directory gate. Any user with a profile is discoverable for DMs.
// ===========================================

// `enabled` lets a surface that mounts outside the community route guard
// (ShareItemDialog on /learn and /assets) skip the read for viewers without
// the community capability — get_users_for_dm returns empty to them anyway
// (hermes mig 587), so the call is wasted.
export function useChatUserSearch(query: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['chat-user-search', query],
    queryFn: () => searchUsersForChat(query),
    enabled: query.length >= 2 && (options?.enabled ?? true),
    staleTime: 30_000,
  });
}

export function useMembersForNewDm(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['members-for-new-dm'],
    queryFn: fetchMembersForNewDm,
    enabled: options?.enabled ?? true,
    staleTime: 60_000,
  });
}

export function useDirectoryMapPins() {
  return useQuery({
    queryKey: communityKeys.directoryMapPins,
    queryFn: fetchDirectoryMapPins,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
}

export function useDirectoryMember(userId: string) {
  return useQuery({
    queryKey: communityKeys.directoryMember(userId),
    queryFn: () => fetchDirectoryMember(userId),
    enabled: !!userId,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
}

/**
 * Admin/super_admin only — a member's email + phone (via the gated RPC).
 * Pass `enabled` = the caller's admin status so non-admins never fire the query.
 */
export function useMemberContact(userId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['community', 'member-contact', userId ?? null],
    queryFn: () => fetchMemberContact(userId!),
    enabled: !!userId && enabled,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });
}

// ===========================================
// Find or Create DM — ERRATA §8.3
// ===========================================

export function useFindOrCreateDm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (targetUserId: string) => findOrCreateDm(targetUserId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community', 'conversations'] });
    },
  });
}

// ===========================================
// Leads Hooks — Phase 6 §7
// ===========================================

export function useLeads(role: 'provider' | 'requester', status?: string) {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: communityKeys.leads(role, status),
    queryFn: () => fetchLeads(role, status),
    enabled: !!user,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

export function useCreateLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateLeadInput) => createLead(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community', 'leads'] });
      queryClient.invalidateQueries({ queryKey: ['community', 'conversations'] });
    },
  });
}

export function useUpdateLeadStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      leadId,
      status,
      extra,
    }: {
      leadId: string;
      status: string;
      extra?: { quoted_amount?: string; lost_reason?: string };
    }) => updateLeadStatus(leadId, status, extra),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community', 'leads'] });
    },
  });
}

// ===========================================
// Community Channels — Phase 4 §1
// ===========================================

/**
 * Fetch community channels, optionally filtered by tier.
 * Returns channels with joined tier info (name, slug).
 */
export function useCommunityChannels(tierId?: string) {
  return useQuery({
    queryKey: communityKeys.channels(tierId),
    queryFn: () => fetchCommunityChannels(tierId),
    staleTime: 5 * 60_000, // 5 min — channels rarely change
    gcTime: 10 * 60_000,
  });
}

// ===========================================
// Message Search — Phase 7 §4
// ERRATA §6.6: query key includes conversationId for scoping
// ===========================================

/**
 * Full-text search across messages.
 * Disabled until query is at least 2 characters.
 * Optional conversationId scopes to a single conversation.
 */
export function useSearchMessages(query: string, conversationId?: string) {
  return useQuery<SearchMessageResult[]>({
    queryKey: communityKeys.searchMessages(query, conversationId),
    queryFn: () => searchMessages(query, { conversationId }),
    enabled: query.trim().length >= 2,
    staleTime: 0, // always fresh search results
    gcTime: 60_000,
  });
}

// ===========================================
// Attachment Hooks — Phase 7 §2
// ERRATA §5.6: 1hr signed URLs, refresh 5min before expiry, cleanup on unmount
// ===========================================

/** Module-level cache shared across all hook instances (ERRATA §5.6) */
const signedUrlCache = new Map<string, { url: string; expiresAt: number }>();

/**
 * Like useSignedUrl but persists the signed URL to localStorage via file-cache,
 * so reopening a chat doesn't re-fetch the URL on every mount. The browser's
 * HTTP cache then handles the image bytes. Targeted at chat IMAGE attachments
 * specifically — voice notes and file downloads still use the in-memory hook.
 *
 * TTL: 6.5 days (12h safety margin under the 7d signed-URL expiry from
 * getAttachmentSignedUrl). The long window means the same storagePath
 * resolves to the same URL across PWA reopens, so the browser HTTP cache
 * keeps hitting on image bytes for ~a week without re-downloading.
 */
export function useChatImageSignedUrl(storagePath: string | null | undefined) {
  const [url, setUrl] = useState<string | null>(() => {
    if (!storagePath) return null;
    return getCachedFileUrl(storagePath);
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!storagePath) {
      setUrl(null);
      return;
    }

    const cached = getCachedFileUrl(storagePath);
    if (cached) {
      setUrl(cached);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    getAttachmentSignedUrl(storagePath)
      .then((signedUrl) => {
        if (cancelled) return;
        // 6.5d — sits just under the 7d signed-URL expiry from
        // getAttachmentSignedUrl. The same storagePath resolves to the same
        // URL for the whole window, so the browser HTTP cache hits the image
        // bytes across PWA reopens.
        setCachedFileUrl(storagePath, signedUrl, 6.5 * 24 * 60 * 60_000);
        setUrl(signedUrl);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Failed to get signed URL'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [storagePath]);

  return { url, loading, error };
}

/**
 * Signed URL hook with auto-refresh and shared cache.
 * ERRATA §5.6: refresh 5min before expiry, cleanup on unmount.
 *
 * `ttlSeconds` (ticket 86d40p593) lets a caller ask for a shorter-lived URL than
 * the seven-day default images rely on. The cache entry is derived from the TTL
 * actually requested rather than hardcoded, so a short TTL cannot leave a stale
 * URL sitting in the cache being handed out after it has expired.
 */
export function useSignedUrl(
  storagePath: string | null | undefined,
  options?: { ttlSeconds?: number },
) {
  const ttlSeconds = options?.ttlSeconds;
  const [url, setUrl] = useState<string | null>(() => {
    if (!storagePath) return null;
    const cached = signedUrlCache.get(storagePath);
    if (cached && cached.expiresAt > Date.now()) return cached.url;
    return null;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const fetchUrl = useCallback(async (path: string) => {
    // Check cache first
    const cached = signedUrlCache.get(path);
    if (cached && cached.expiresAt > Date.now() + 5 * 60_000) {
      setUrl(cached.url);
      return cached;
    }

    setLoading(true);
    setError(null);
    try {
      const signedUrl = await getAttachmentSignedUrl(path, ttlSeconds);
      // Track the real expiry. Capped at an hour so a seven-day image URL is
      // still re-minted periodically, floored at the requested TTL so a short
      // one is never cached past its own life.
      const effectiveTtlMs = Math.min((ttlSeconds ?? 3600) * 1000, 60 * 60_000);
      const expiresAt = Date.now() + effectiveTtlMs;
      signedUrlCache.set(path, { url: signedUrl, expiresAt });
      setUrl(signedUrl);
      return { url: signedUrl, expiresAt };
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to get signed URL'));
      return null;
    } finally {
      setLoading(false);
    }
  }, [ttlSeconds]);

  useEffect(() => {
    if (!storagePath) {
      setUrl(null);
      return;
    }

    let cancelled = false;

    const scheduleRefresh = (expiresAt: number) => {
      clearTimeout(timerRef.current);
      // Refresh 5 minutes before expiry
      const refreshIn = Math.max(expiresAt - Date.now() - 5 * 60_000, 0);
      timerRef.current = setTimeout(async () => {
        if (cancelled) return;
        const result = await fetchUrl(storagePath);
        if (result && !cancelled) scheduleRefresh(result.expiresAt);
      }, refreshIn);
    };

    (async () => {
      const result = await fetchUrl(storagePath);
      if (result && !cancelled) scheduleRefresh(result.expiresAt);
    })();

    return () => {
      cancelled = true;
      clearTimeout(timerRef.current);
    };
  }, [storagePath, fetchUrl]);

  return { url, loading, error };
}

// ===========================================
// Block & Report Hooks — Phase 8a
// ===========================================

/**
 * Check if a specific user is blocked by the current user.
 */
export function useIsUserBlocked(userId: string | undefined) {
  return useQuery<boolean, Error>({
    queryKey: communityKeys.isBlocked(userId!),
    queryFn: () => isUserBlocked(userId!),
    enabled: !!userId,
    staleTime: 60_000,
  });
}

/**
 * Block a user. Invalidates conversations (blocked user's DMs should disappear).
 */
export function useBlockUser() {
  const queryClient = useQueryClient();

  return useMutation<UserBlock, Error, string>({
    mutationFn: (blockedUserId: string) => blockUser(blockedUserId),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: communityKeys.conversations(),
      });
    },
  });
}

/**
 * Unblock a user. Invalidates conversations.
 */
export function useUnblockUser() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (blockedUserId: string) => unblockUser(blockedUserId),
    onSettled: (_d, _e, blockedUserId) => {
      queryClient.invalidateQueries({
        queryKey: communityKeys.conversations(),
      });
      queryClient.invalidateQueries({
        queryKey: communityKeys.isBlocked(blockedUserId),
      });
    },
  });
}

/**
 * Report content (user, conversation, or message) for moderation.
 */
export function useReportContent() {
  return useMutation<ReportReceipt, Error, ReportContentInput>({
    mutationFn: (input: ReportContentInput) => reportContent(input),
  });
}

// ===========================================
// Group chats (hermes migration 647)
// ===========================================
// Each mutation refreshes the chat list and the open conversation. The server
// writes the system line, which also arrives over realtime.

function useGroupMutation<TVars>(
  mutationFn: (vars: TVars) => Promise<unknown>,
  conversationIdOf: (vars: TVars) => string | null,
) {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, TVars>({
    mutationFn,
    onSettled: (_data, _err, vars) => {
      queryClient.invalidateQueries({ queryKey: communityKeys.conversations() });
      const id = conversationIdOf(vars);
      if (id) queryClient.invalidateQueries({ queryKey: communityKeys.conversation(id) });
    },
  });
}

export function useCreateGroup() {
  const queryClient = useQueryClient();
  return useMutation<{ id: string }, Error, GroupDetailsInput & { memberIds: string[] }>({
    mutationFn: (input) => createGroupConversation(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: communityKeys.conversations() });
    },
  });
}

export function useAddGroupMembers() {
  return useGroupMutation(
    ({ conversationId, userIds }: { conversationId: string; userIds: string[] }) =>
      addGroupMembers(conversationId, userIds),
    (v) => v.conversationId,
  );
}

export function useRemoveGroupMember() {
  return useGroupMutation(
    ({ conversationId, userId }: { conversationId: string; userId: string }) =>
      removeGroupMember(conversationId, userId),
    (v) => v.conversationId,
  );
}

export function useSetGroupMemberRole() {
  return useGroupMutation(
    ({ conversationId, userId, role }: { conversationId: string; userId: string; role: 'admin' | 'member' }) =>
      setGroupMemberRole(conversationId, userId, role),
    (v) => v.conversationId,
  );
}

export function useUpdateGroup() {
  return useGroupMutation(
    ({ conversationId, ...input }: GroupDetailsInput & { conversationId: string }) =>
      updateGroup(conversationId, input),
    (v) => v.conversationId,
  );
}

export function useLeaveGroup() {
  return useGroupMutation(
    (conversationId: string) => leaveGroup(conversationId),
    (id) => id,
  );
}

/**
 * "Remove from list" after leaving a group. Prunes the row optimistically so
 * the list never flashes it back while the refetch is in flight.
 */
export function useRemoveGroupFromList() {
  const queryClient = useQueryClient();
  return useMutation<
    void,
    Error,
    string,
    { previous: Array<[QueryKey, ConversationListItem[] | undefined]> }
  >({
    mutationFn: (conversationId) => removeGroupFromList(conversationId),
    onMutate: async (conversationId) => {
      await queryClient.cancelQueries({ queryKey: communityKeys.conversations() });
      const previous = queryClient.getQueriesData<ConversationListItem[]>({
        queryKey: communityKeys.conversations(),
      });
      for (const [key, list] of previous) {
        if (!list) continue;
        queryClient.setQueryData<ConversationListItem[]>(key, list.filter((c) => c.id !== conversationId));
      }
      return { previous };
    },
    onError: (_err, _id, context) => {
      context?.previous.forEach(([key, list]) => queryClient.setQueryData(key, list));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: communityKeys.conversations() });
    },
  });
}

// ===========================================
// Org membership and tier
// ===========================================

/**
 * The current user's org membership (id + revenue/size) in one query. Canonical
 * source the org-id and revenue-range hooks derive from — see
 * communityKeys.myOrgMembership. Replaces the 3–4 duplicate organization_members
 * reads that fired on every community load (2026-07-02 audit).
 */
export function useMyOrgMembership() {
  return useQuery<MyOrgMembership | null>({
    queryKey: communityKeys.myOrgMembership,
    queryFn: fetchMyOrgMembership,
    staleTime: 10 * 60_000,
  });
}

const EMPTY_ROSTER: OrgMember[] = [];

/**
 * Fetch members of the current user's organization. The org id comes from the
 * shared membership query (no separate id lookup); the roster is a dependent
 * query keyed by org id.
 */
export function useOrgMembers() {
  const membership = useMyOrgMembership();
  const orgId = membership.data?.organizationId ?? null;
  const roster = useQuery<OrgMember[]>({
    queryKey: [...communityKeys.orgMembers, orgId ?? 'none'] as const,
    queryFn: () => fetchOrgRoster(orgId as string),
    enabled: !!orgId,
    staleTime: 5 * 60_000,
  });
  // The membership lookup is part of this hook's load: while it resolves, the
  // disabled roster query alone would report isLoading:false with no data, and
  // an org-less user would stay `undefined` forever (pre-split behavior was []).
  return {
    ...roster,
    data: membership.isSuccess && !orgId ? EMPTY_ROSTER : roster.data,
    isLoading: membership.isLoading || roster.isLoading,
  };
}

/**
 * Current user's organization ID — a select over the shared membership query,
 * so it costs no extra network beyond useMyOrgMembership.
 */
export function useUserOrganizationId() {
  return useQuery<MyOrgMembership | null, Error, string | null>({
    queryKey: communityKeys.myOrgMembership,
    queryFn: fetchMyOrgMembership,
    staleTime: 10 * 60_000,
    select: (m) => m?.organizationId ?? null,
  });
}

/**
 * Fetch the current user's community tier info.
 */
export function useUserTier() {
  return useQuery<UserTierInfo>({
    queryKey: communityKeys.userTier,
    queryFn: fetchUserTier,
    staleTime: 5 * 60_000,
  });
}

/**
 * Current user's org revenue range for pod derivation — a select over the shared
 * membership query. Falls back to deriving from company_size when the explicit
 * annual_revenue_range is null (previously an async dynamic import; the pods
 * helper is now statically imported so the fallback runs inside select).
 */
export function useUserOrgRevenueRange() {
  return useQuery<MyOrgMembership | null, Error, string | null>({
    queryKey: communityKeys.myOrgMembership,
    queryFn: fetchMyOrgMembership,
    staleTime: 10 * 60_000,
    select: (m) =>
      m
        ? (m.annualRevenueRange ?? revenueRangeFromCompanySize(m.companySize))
        : null,
  });
}

/**
 * Fetch a map of user_id → revenue_range for all org members.
 * Used for client-side pod filtering on conversation lists.
 */
export function useParticipantRevenueMap() {
  return useQuery<Map<string, string>>({
    queryKey: communityKeys.participantRevenueMap,
    queryFn: fetchParticipantRevenueMap,
    staleTime: 5 * 60_000,
  });
}

/**
 * Fetch community channels enriched with required_tier_level for gating.
 */
export function useCommunityChannelsWithTiers() {
  return useQuery({
    queryKey: communityKeys.channelsWithTiers,
    queryFn: fetchCommunityChannelsWithTiers,
    staleTime: 5 * 60_000,
  });
}

/**
 * Join a tier's chat conversation. Lazy-creates the conversation if it
 * doesn't exist yet, then adds the current user as a participant.
 */
export function useJoinTier() {
  const queryClient = useQueryClient();

  return useMutation<DMResult, Error, string>({
    mutationFn: (tierId: string) => getOrCreateTierConversation(tierId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['community', 'conversations'],
        refetchType: 'all',
      });
    },
  });
}

// ===========================================
// Community Feed — Posts, Reactions, Comments
// ===========================================

/**
 * Infinite query for feed posts with cursor-based pagination.
 * Ordered: pinned first, then newest.
 */
export function usePosts(
  tierId: string | undefined,
  channelId?: string,
  pods?: PodNumber[],
  /** Custom-community scope — the all-channels view filters by community_id. */
  communityId?: string,
) {
  return useInfiniteQuery<FeedPostPage>({
    // Community feeds get their own key namespace so tier invalidation
    // prefixes never collide with community ones.
    queryKey: communityKeys.posts(
      communityId ? `community:${communityId}` : tierId ?? '',
      channelId,
      pods,
    ),
    queryFn: ({ pageParam }) =>
      fetchPosts(tierId ?? '', pageParam as string | undefined, 15, channelId, pods, communityId),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!tierId || !!communityId,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    placeholderData: (prev) => prev, // keep previous data while switching pods
  });
}

/**
 * Fetch a single post by id — used by the /community/post/$postId deep-link
 * page. RLS returns null for posts the user can't access; consumers treat
 * null as "not found or access denied".
 */
export function usePost(postId: string | undefined) {
  return useQuery<FeedPost | null>({
    queryKey: communityKeys.post(postId ?? ''),
    queryFn: () => fetchPost(postId!),
    enabled: !!postId,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

/**
 * Create a new post. Invalidates the posts list on success.
 */
export function useCreatePost() {
  const queryClient = useQueryClient();

  return useMutation<
    FeedPost,
    Error,
    { tierId: string | null; content: string; images?: PostImage[]; postType?: 'post' | 'announcement' | 'poll'; channelId?: string; attachments?: PostAttachment[]; location?: PostLocation | null; title?: string | null; mentions?: Mention[]; communityId?: string | null; referencedEvent?: { eventId: string; occurrenceStart?: string | null } | null; speedyId?: string | null }
  >({
    mutationFn: ({ tierId, content, images, postType, channelId, attachments, location, title, mentions, communityId, referencedEvent, speedyId }) =>
      createPost(tierId, content, images, postType, channelId, attachments, location, title, mentions, communityId, referencedEvent, speedyId),
    onSuccess: (data, { tierId, communityId, speedyId }) => {
      // Invalidate all channel variants for this tier (prefix match). For
      // global posts (tierId null) we skip the tier-keyed invalidation —
      // the per-tier list views will refetch on next mount; the user's
      // current view is already invalidated by the hubCards entry below.
      if (tierId) {
        queryClient.invalidateQueries({
          queryKey: ['community', 'posts', tierId],
        });
      }
      if (communityId) {
        queryClient.invalidateQueries({
          queryKey: ['community', 'posts', `community:${communityId}`],
        });
        queryClient.invalidateQueries({ queryKey: customCommunityKeys.hub });
      }
      queryClient.invalidateQueries({
        queryKey: communityKeys.hubCards,
      });
      if (data?.id) {
        api.logActivity({
          type: 'community',
          action: 'created',
          entity_type: 'community_post',
          entity_id: data.id,
          entity_name: 'Post',
          description: 'Community post created',
          metadata: { tier_id: tierId, ...(speedyId ? { speedy_id: speedyId } : {}) },
        }).catch(() => {});
      }
    },
  });
}

type UpdatePostInput = {
  postId: string;
  tierId: string;
  title?: string | null;
  content?: string;
  images?: PostImage[];
  attachments?: PostAttachment[];
  location?: PostLocation | null;
  mentions?: Mention[];
  channelId?: string | null;
  // Optional optimistic preview applied to the cached row immediately so
  // the user sees their edit before the server confirms. Includes blob-URL
  // images for any newly-added local files; reconciled to real storage paths
  // on success.
  optimisticPreview?: Partial<FeedPost>;
};

type UpdatePostContext = {
  snapshots: Array<[readonly unknown[], InfiniteData<FeedPostPage> | undefined]>;
};

/**
 * Edit a post. Author-only (RLS-enforced). Optimistic-updates the cached
 * feed via `optimisticPreview` on mutate and reconciles to the server-
 * confirmed row on success; rolls back on error.
 */
export function useUpdatePost() {
  const queryClient = useQueryClient();

  return useMutation<UpdatedPost, Error, UpdatePostInput, UpdatePostContext>({
    mutationFn: ({ postId, title, content, images, attachments, location, mentions, channelId }) =>
      updatePost(postId, { title, content, images, attachments, location, mentions, channelId }),
    onMutate: async ({ postId, tierId, optimisticPreview }) => {
      await queryClient.cancelQueries({
        queryKey: ['community', 'posts', tierId],
      });
      const snapshots = queryClient.getQueriesData<InfiniteData<FeedPostPage>>({
        queryKey: ['community', 'posts', tierId],
      });
      if (optimisticPreview) {
        queryClient.setQueriesData<InfiniteData<FeedPostPage>>(
          { queryKey: ['community', 'posts', tierId] },
          (old) => {
            if (!old) return old;
            return {
              ...old,
              pages: old.pages.map((page) => ({
                ...page,
                posts: page.posts.map((p) =>
                  p.id === postId ? { ...p, ...optimisticPreview } : p,
                ),
              })),
            };
          },
        );
      }
      return { snapshots };
    },
    onError: (_err, _vars, context) => {
      if (!context?.snapshots) return;
      for (const [key, data] of context.snapshots) {
        queryClient.setQueryData(key as readonly unknown[], data);
      }
    },
    onSuccess: (data, { tierId, channelId }) => {
      queryClient.setQueriesData<InfiniteData<FeedPostPage>>(
        { queryKey: ['community', 'posts', tierId] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              posts: page.posts.map((p) =>
                p.id === data.id
                  ? {
                      ...p,
                      title: data.title,
                      content: data.content,
                      images: data.images,
                      attachments: data.attachments,
                      location: data.location,
                      updated_at: data.updated_at,
                    }
                  : p,
              ),
            })),
          };
        },
      );
      // A channel move changes which feed the post belongs to, and the patch
      // above only rewrites the row in place. Refetch so it leaves the old
      // channel instead of lingering there until a reload.
      if (channelId !== undefined) {
        queryClient.invalidateQueries({ queryKey: ['community', 'posts'] });
      }
    },
  });
}

/**
 * Communities (tier or custom) where the current user is a chat admin/owner.
 * Cached like useIsSuperAdmin; the feed derives System-post edit-button
 * visibility from this with no per-post round trips. The server RPC
 * (admin_update_system_post) is the authoritative gate.
 */
export function useMyChatAdminScopes() {
  const { data: user } = useCurrentUser();
  return useQuery({
    queryKey: ['community', 'my-chat-admin-scopes', user?.id ?? null],
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: fetchMyChatAdminScopes,
  });
}

/**
 * Edit a System recap post (title + content) via admin_update_system_post
 * (mig 401). Patches the cached feed/post entries on success, mirroring
 * useUpdatePost.
 */
export function useUpdateSystemPost() {
  const queryClient = useQueryClient();
  return useMutation<
    void,
    Error,
    {
      postId: string;
      tierId: string;
      title: string | null;
      content: string;
      channelId?: string | null;
      // Rich fields, added with migration 651. The composer offers attach/photo/location on a
      // system post exactly as on any other, and before 651 they were silently dropped.
      images?: PostImage[];
      attachments?: PostAttachment[];
      location?: PostLocation | null;
      mentions?: Mention[];
    }
  >({
    mutationFn: ({ postId, title, content, channelId, images, attachments, location, mentions }) =>
      updateSystemPost(postId, {
        title,
        content,
        channelId,
        images,
        attachments,
        location,
        mentions,
      }),
    onSuccess: (_data, { postId, tierId, title, content, channelId }) => {
      const patch = { title, content, updated_at: new Date().toISOString() };
      queryClient.setQueriesData<InfiniteData<FeedPostPage>>(
        { queryKey: ['community', 'posts', tierId] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              posts: page.posts.map((p) =>
                p.id === postId ? { ...p, ...patch } : p,
              ),
            })),
          };
        },
      );
      queryClient.invalidateQueries({ queryKey: communityKeys.post(postId) });
      // Same reason as useUpdatePost: a channel move has to leave the old feed.
      if (channelId !== undefined) {
        queryClient.invalidateQueries({ queryKey: ['community', 'posts'] });
      }
    },
  });
}

/**
 * Soft-delete a comment. Invalidates the post's comment list on success.
 * Permission is enforced server-side by the soft_delete_comment RPC
 * (author / super_admin / org-admin of the comment author's org).
 */
export function useDeleteComment() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { commentId: string; postId: string }>({
    mutationFn: ({ commentId }) => deleteComment(commentId),
    onSuccess: (_data, { postId }) => {
      queryClient.invalidateQueries({ queryKey: communityKeys.postComments(postId) });
      // comment_count lives on the post detail + the feed lists — refresh both
      // so the count updates immediately, not only on a manual reload.
      queryClient.invalidateQueries({ queryKey: communityKeys.post(postId) });
      queryClient.invalidateQueries({ queryKey: ['community', 'posts'] });
    },
  });
}

/**
 * Soft-delete a post. Invalidates posts on success.
 */
export function useDeletePost() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { postId: string; tierId: string }>({
    mutationFn: ({ postId }) => deletePost(postId),
    onSuccess: (_data, { postId, tierId }) => {
      queryClient.invalidateQueries({
        queryKey: ['community', 'posts', tierId],
      });
      // Drop the deep-link cache entry too — otherwise re-opening
      // /community/post/<id> within gcTime re-renders the deleted post.
      queryClient.removeQueries({ queryKey: communityKeys.post(postId) });
    },
  });
}

/**
 * Toggle a reaction on a post with optimistic update.
 */
export function useTogglePostReaction() {
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser();

  return useMutation<
    'added' | 'removed',
    Error,
    { postId: string; emoji: string; tierId: string; currentlyReacted: boolean }
  >({
    mutationFn: ({ postId, emoji }) =>
      togglePostReaction(postId, emoji),
    onMutate: async ({ postId, emoji, tierId, currentlyReacted }) => {
      await queryClient.cancelQueries({
        queryKey: ['community', 'posts', tierId],
      });

      // Optimistic update on the infinite data (applies to whichever channel cache is active)
      const activeKey = queryClient.getQueryCache().findAll({ queryKey: ['community', 'posts', tierId] })
        .find(q => q.state.data)?.queryKey as readonly unknown[] | undefined;
      queryClient.setQueryData<InfiniteData<FeedPostPage>>(
        activeKey ?? communityKeys.posts(tierId),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              posts: page.posts.map((p) => {
                if (p.id !== postId) return p;
                let newReactions = [...p.reactions];
                if (currentlyReacted) {
                  // Remove
                  newReactions = newReactions
                    .map(r => r.emoji === emoji ? { ...r, count: r.count - 1, reacted_by_me: false } : r)
                    .filter(r => r.count > 0);
                } else {
                  // Add
                  const existing = newReactions.find(r => r.emoji === emoji);
                  if (existing) {
                    newReactions = newReactions.map(r =>
                      r.emoji === emoji ? { ...r, count: r.count + 1, reacted_by_me: true } : r,
                    );
                  } else {
                    newReactions.push({ emoji, count: 1, reacted_by_me: true });
                  }
                }
                return { ...p, reactions: newReactions };
              }),
            })),
          };
        },
      );
    },
    onSettled: (_data, _err, { postId, tierId }) => {
      queryClient.invalidateQueries({
        queryKey: ['community', 'posts', tierId],
      });
      // Drop the cached reactors list so the next hover refetches.
      queryClient.invalidateQueries({
        queryKey: ['community', 'post', postId, 'reactors'],
      });
    },
  });
}

/**
 * Reactors list for a post — for the hover-card "who reacted" popover.
 * Cached aggressively (5 min staleTime) so a quick re-hover hits cache.
 * Invalidated when togglePostReaction succeeds.
 */
export function usePostReactors(
  postId: string | undefined,
  enabled: boolean,
) {
  return useQuery<PostReactor[]>({
    queryKey: ['community', 'post', postId ?? '', 'reactors'] as const,
    queryFn: () => fetchPostReactors(postId!),
    enabled: !!postId && enabled,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });
}

/**
 * Reactors list for a chat message — for the hover-card "who reacted" popover.
 * Mirrors usePostReactors. `enabled` is gated by the hover state so the
 * network call only fires when the popover opens.
 * Invalidated when useAddReaction / useRemoveReaction settle.
 */
export function useMessageReactors(
  messageId: string | undefined,
  enabled: boolean,
) {
  return useQuery<Reactor[]>({
    queryKey: ['community', 'message', messageId ?? '', 'reactors'] as const,
    queryFn: () => fetchMessageReactors(messageId!),
    enabled: !!messageId && enabled,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });
}

/**
 * Fetch comments for a post.
 */
export function usePostComments(postId: string | undefined) {
  return useQuery<PostCommentWithAuthor[]>({
    queryKey: communityKeys.postComments(postId ?? ''),
    queryFn: () => fetchPostComments(postId!),
    enabled: !!postId,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

/**
 * Add a comment to a post. Invalidates comments + posts (for comment count).
 */
export function useAddPostComment() {
  const queryClient = useQueryClient();

  return useMutation<
    PostCommentWithAuthor,
    Error,
    {
      postId: string;
      content: string;
      parentCommentId?: string;
      tierId: string;
      mentions?: Mention[];
      images?: PostImage[];
      attachments?: PostAttachment[];
      location?: PostLocation | null;
    }
  >({
    mutationFn: ({ postId, content, parentCommentId, mentions, images, attachments, location }) =>
      addPostComment(postId, content, parentCommentId, mentions, images, attachments, location),
    onSuccess: (_data, { postId, tierId }) => {
      queryClient.invalidateQueries({
        queryKey: communityKeys.postComments(postId),
      });
      // Update comment count in the feed list + the single-post detail.
      queryClient.invalidateQueries({
        queryKey: communityKeys.posts(tierId),
      });
      queryClient.invalidateQueries({
        queryKey: communityKeys.post(postId),
      });
    },
  });
}

/**
 * Fetch community tiers for hub cards.
 */
export function useCommunityTiersForHub() {
  return useQuery<CommunityHubCard[]>({
    queryKey: communityKeys.hubCards,
    queryFn: fetchCommunityTiersForHub,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
}

/**
 * Fetch every tier's DB visual identity (badge + accent) for the tier-config
 * override store. Brand identity changes rarely, so cache it hard; TierIdentityLoader
 * pushes the result into the store.
 */
export function useTierIdentities() {
  return useQuery<TierIdentityRow[]>({
    queryKey: ['tier-identities'],
    queryFn: fetchTierIdentities,
    staleTime: 30 * 60_000,
    gcTime: 60 * 60_000,
  });
}

/**
 * super_admin mutation: set a tier's badge + accent. Invalidates the identity
 * query (TierIdentityLoader re-applies overrides app-wide) and the hub cards.
 */
export function useSetTierIdentity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { code: string; iconUrl: string | null; accentColor: string | null }) =>
      setTierIdentity(vars.code, vars.iconUrl, vars.accentColor),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tier-identities'] });
      queryClient.invalidateQueries({ queryKey: communityKeys.hubCards });
    },
  });
}

/**
 * Resolve the caller's highest-level active community tier membership.
 * Returns null when the user has no active community memberships.
 */
export function useMyTopTierCommunity() {
  return useQuery({
    queryKey: ['my-top-tier-community'] as const,
    queryFn: getMyTopTierCommunity,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });
}

/**
 * On first community page entry: join the user's top tier + 3 globals.
 * Fire-and-forget. Idempotent.
 */
export function useAutoJoinTopTierAndGlobals() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (tierId: string) => autoJoinTopTierAndGlobals(tierId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['community', 'conversations'],
        refetchType: 'all',
      });
    },
  });
}

/**
 * Upload post images to Supabase Storage.
 * Returns array of PostImage objects with storage paths.
 */
export function useUploadPostImages() {
  return useMutation<PostImage[], Error, File[]>({
    mutationFn: async (files: File[]) => {
      const results = await Promise.all(files.map(f => uploadPostImage(f)));
      return results;
    },
  });
}

/**
 * Upload post attachments (files) to Supabase Storage.
 * Returns array of PostAttachment objects with storage paths.
 */
export function useUploadPostAttachments() {
  return useMutation<PostAttachment[], Error, File[]>({
    mutationFn: async (files: File[]) => {
      const results = await Promise.all(
        files.map(async (f) => {
          const att = await uploadPostAttachment(f);
          // Video attachments: kick off the background HLS transcode and stamp
          // the assetId so the feed post upgrades raw → HLS once ready. Covers
          // both the create and edit paths (both route through this hook).
          if (att.mimeType?.startsWith('video/')) {
            const assetId = await enqueueUserVideoTranscode({
              surface: 'feed',
              storagePath: att.storagePath,
              originalFilename: att.filename,
              originalSizeBytes: att.sizeBytes,
              originalMimeType: att.mimeType,
            });
            return { ...att, assetId };
          }
          return att;
        }),
      );
      return results;
    },
  });
}

// ===========================================
// Story Hooks (24hr ephemeral content)
// ===========================================

/**
 * Fetch active stories for a community tier, grouped by author.
 */
export function useStories(tierId: string | undefined) {
  return useQuery<StoryGroup[]>({
    queryKey: communityKeys.stories(tierId ?? ''),
    queryFn: () => fetchStories(tierId!),
    enabled: !!tierId,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Create a new story. Invalidates stories list on success.
 */
export function useCreateStory() {
  const queryClient = useQueryClient();

  return useMutation<
    StoryItem,
    Error,
    { tierId: string; input: CreateStoryInput }
  >({
    mutationFn: ({ tierId, input }) => createStory(tierId, input),
    onSuccess: (_data, { tierId }) => {
      queryClient.invalidateQueries({
        queryKey: communityKeys.stories(tierId),
      });
    },
  });
}

/**
 * Delete a story (soft delete). Invalidates stories list on success.
 */
export function useDeleteStory() {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    { storyId: string; tierId: string }
  >({
    mutationFn: ({ storyId }) => deleteStory(storyId),
    onSuccess: (_data, { tierId }) => {
      queryClient.invalidateQueries({
        queryKey: communityKeys.stories(tierId),
      });
    },
  });
}

/**
 * Record that the current user has viewed a story.
 * Fire-and-forget — optimistically marks as viewed in cache.
 */
export function useRecordStoryView() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { storyId: string; tierId: string }>({
    mutationFn: ({ storyId }) => recordStoryView(storyId),
    onSuccess: (_data, { tierId }) => {
      // Refetch to update has_unviewed flags
      queryClient.invalidateQueries({
        queryKey: communityKeys.stories(tierId),
      });
    },
  });
}

/**
 * Fetch viewers of a story (for "seen by" UI).
 */
export function useStoryViewers(storyId: string | undefined) {
  return useQuery<StoryViewer[]>({
    queryKey: communityKeys.storyViewers(storyId ?? ''),
    queryFn: () => fetchStoryViewers(storyId!),
    enabled: !!storyId,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

/**
 * Upload a story image to Supabase Storage.
 * Returns the storage path.
 */
export function useUploadStoryImage() {
  return useMutation<string, Error, File>({
    mutationFn: (file: File) => uploadStoryImage(file),
  });
}

/**
 * Fetch stories across ALL accessible community tiers in parallel.
 * Uses useQueries to fire one fetchStories(tierId) per hub card.
 * Shares cache keys with per-tier useStories() — no redundant fetches.
 */
export function useAllStories(hubCards: CommunityHubCard[] | undefined) {
  const cards = hubCards ?? [];

  const results = useQueries({
    queries: cards.map(card => ({
      queryKey: communityKeys.stories(card.id),
      queryFn: () => fetchStories(card.id),
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      enabled: cards.length > 0,
    })),
  });

  const isLoading = results.some(r => r.isLoading);

  const tierStoryGroups: TierStoryGroup[] = cards
    .map((card, i) => ({
      tier_id: card.id,
      tier_code: card.code,
      tier_name: card.name,
      tier_level: card.tier_level,
      story_groups: results[i]?.data ?? [],
    }))
    .filter(t => t.story_groups.length > 0);

  return { data: tierStoryGroups, isLoading };
}

/**
 * Aggregated emoji reactions for a tier's stories, keyed by story id.
 * `storyIds` is derived from the tier's loaded story groups; keyed by tierId so
 * the viewer (pills + hover) and the scroller (avatar top-2 + count) share one cache.
 */
export function useStoryReactions(
  tierId: string | undefined,
  storyIds: string[],
) {
  return useQuery<StoryReactionsByStory>({
    queryKey: communityKeys.storyReactions(tierId ?? ''),
    queryFn: () => fetchStoryReactions(storyIds),
    enabled: !!tierId && storyIds.length > 0,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

/**
 * Toggle the current user's emoji reaction on a story, with optimistic update
 * of the tier's aggregated reaction cache (drives both the in-viewer pills and
 * the avatar top-2 + count instantly).
 */
export function useToggleStoryReaction() {
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser();

  return useMutation<
    'added' | 'removed',
    Error,
    { storyId: string; emoji: string; tierId: string; currentlyReacted: boolean }
  >({
    mutationFn: ({ storyId, emoji }) => toggleStoryReaction(storyId, emoji),
    onMutate: async ({ storyId, emoji, tierId, currentlyReacted }) => {
      const key = communityKeys.storyReactions(tierId);
      await queryClient.cancelQueries({ queryKey: key });
      const uid = user?.id ?? '';

      queryClient.setQueryData<StoryReactionsByStory>(key, (old) => {
        const map: StoryReactionsByStory = { ...(old ?? {}) };
        let list = [...(map[storyId] ?? [])];
        if (currentlyReacted) {
          list = list
            .map(r =>
              r.emoji === emoji
                ? { ...r, count: r.count - 1, reacted: false, userIds: r.userIds.filter(u => u !== uid) }
                : r,
            )
            .filter(r => r.count > 0);
        } else {
          const existing = list.find(r => r.emoji === emoji);
          if (existing) {
            list = list.map(r =>
              r.emoji === emoji
                ? { ...r, count: r.count + 1, reacted: true, userIds: [...r.userIds, uid] }
                : r,
            );
          } else {
            list = [...list, { emoji, count: 1, reacted: true, userIds: [uid] }];
          }
        }
        map[storyId] = list.sort((a, b) => b.count - a.count);
        return map;
      });
    },
    onSettled: (_data, _err, { tierId }) => {
      queryClient.invalidateQueries({ queryKey: communityKeys.storyReactions(tierId) });
    },
  });
}

// ===========================================
// Community Engagement / Gamification
// ===========================================

export function useCommunityLeaderboard(timeframe: 'all_time' | 'monthly' | 'weekly' = 'monthly') {
  return useQuery({
    queryKey: communityKeys.leaderboard(timeframe),
    queryFn: () => fetchCommunityLeaderboard(timeframe),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
}

export function useMyCommunityStats() {
  // Gated on auth: Home mounts this and the root route briefly renders the
  // authed layout pre-login — an ungated query fired an anon
  // get_my_community_stats RPC on the login screen (2026-07-02 audit, finding 6).
  const { data: user } = useCurrentUser();
  return useQuery({
    queryKey: communityKeys.myStats(),
    queryFn: () => fetchMyCommunityStats(),
    enabled: !!user,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

/** The Home checklist's "Say hello in the community" item. See fetchHasCommunityActivity. */
export function useHasCommunityActivity() {
  const { data: user } = useCurrentUser();
  return useQuery({
    queryKey: communityKeys.hasActivity(),
    queryFn: () => fetchHasCommunityActivity(),
    enabled: !!user,
    // Nothing that posts or sends invalidates this key, so read again each time Home opens:
    // a member who posts and comes straight back sees the item ticked.
    refetchOnMount: 'always',
    gcTime: 5 * 60_000,
  });
}

export function useCommunityBadges() {
  return useQuery({
    queryKey: communityKeys.badges(),
    queryFn: () => fetchCommunityBadges(),
    staleTime: 60_000,
    gcTime: 10 * 60_000,
  });
}

export function useCommunityPerks() {
  return useQuery({
    queryKey: communityKeys.perks(),
    queryFn: () => fetchCommunityPerks(),
    staleTime: 60_000,
    gcTime: 10 * 60_000,
  });
}

export function usePurchaseCommunityPerk() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ perkId, cost }: { perkId: string; cost: number }) =>
      purchaseCommunityPerk(perkId, cost),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community', 'perks'] });
      queryClient.invalidateQueries({ queryKey: ['community', 'myStats'] });
    },
  });
}


// ===========================================
// Channel Settings Hooks (migrations 204 + 205)
// ===========================================

export function useChannelSettings(channelId: string | undefined) {
  return useQuery({
    queryKey: ['community', 'channelSettings', channelId],
    queryFn: () => fetchChannelSettings(channelId as string),
    enabled: !!channelId,
    staleTime: 30_000,
  });
}

export function useUpdateChannelSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ channelId, patch }: { channelId: string; patch: UpdateChannelSettingsPatch }) =>
      updateChannelSettings(channelId, patch),
    onSuccess: (_data, { channelId }) => {
      queryClient.invalidateQueries({
        queryKey: ['community', 'channelSettings', channelId],
      });
    },
  });
}

export function useChannelMembers(channelId: string | undefined) {
  return useQuery({
    queryKey: ['community', 'channelMembers', channelId],
    queryFn: () => fetchChannelMembers(channelId as string),
    enabled: !!channelId,
    staleTime: 10_000,
  });
}

export function useAddableCommunityUsers(channelId: string | undefined, query: string) {
  return useQuery({
    queryKey: ['community', 'addableUsers', channelId, query],
    queryFn: () => searchAddableCommunityUsers(channelId as string, query),
    enabled: !!channelId && query.length >= 1,
    staleTime: 5_000,
  });
}

/** Name search for create-flow people pickers (no target exists yet). */
export function useSearchCommunityUsers(query: string) {
  return useQuery({
    queryKey: ['community', 'userSearch', query] as const,
    queryFn: () => searchCommunityUsers(query),
    enabled: query.trim().length >= 1,
    staleTime: 10_000,
  });
}

export function useAddChannelMembers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ channelId, userIds }: { channelId: string; userIds: string[] }) =>
      addChannelMembers(channelId, userIds),
    onSuccess: (_data, { channelId }) => {
      queryClient.invalidateQueries({
        queryKey: ['community', 'channelMembers', channelId],
      });
      queryClient.invalidateQueries({
        queryKey: ['community', 'addableUsers', channelId],
      });
    },
  });
}

export function useRemoveChannelMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      channelId,
      userId,
      archivePosts,
    }: {
      channelId: string;
      userId: string;
      archivePosts: boolean;
    }) => removeChannelMember(channelId, userId, archivePosts),
    onSuccess: (_archivedCount, { channelId }) => {
      queryClient.invalidateQueries({
        queryKey: ['community', 'channelMembers', channelId],
      });
      // If posts were archived, the feed needs to refresh too
      queryClient.invalidateQueries({ queryKey: ['community', 'posts'] });
    },
  });
}

export function useUpdateChannelMemberRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      channelId,
      userId,
      role,
    }: {
      channelId: string;
      userId: string;
      role: ChannelMemberRole;
    }) => updateChannelMemberRole(channelId, userId, role),
    onSuccess: (_data, { channelId }) => {
      queryClient.invalidateQueries({
        queryKey: ['community', 'channelMembers', channelId],
      });
    },
  });
}

// ===========================================
// Channel Management — CRUD + archive
// ===========================================

import {
  fetchTierChannels,
  fetchArchivedTierChannels,
  createChannel,
  updateChannel,
  archiveChannel,
  unarchiveChannel,
  type CommunityChannelRow,
  type ChannelCreateInput,
  type ChannelUpdateInput,
} from './community';

/** React Query keys for the channel-management surfaces. */
const channelMgmtKeys = {
  tierChannels: (tierId: string | null) =>
    ['community', 'tierChannels', tierId ?? 'global'] as const,
  archivedChannels: (tierId: string | null) =>
    ['community', 'archivedChannels', tierId ?? 'global'] as const,
  channelTierId: (channelId: string) =>
    ['community', 'channelTierId', channelId] as const,
};

/**
 * Look up the tier_id for a given channel so the Channel Management section
 * can scope its list. Returns `null` for global channels. Single-shot query.
 */
export function useChannelTierId(channelId: string) {
  return useQuery<string | null, Error>({
    queryKey: channelMgmtKeys.channelTierId(channelId),
    queryFn: async () => {
      const { supabase } = await import('@/lib/supabase');
      const { data, error } = await supabase
        .from('community_channels')
        .select('community_tier_id')
        .eq('id', channelId)
        .maybeSingle();
      if (error) throw error;
      return (data?.community_tier_id ?? null) as string | null;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch all ACTIVE channels in a tier (or the global bucket when tierId is
 * null). Sorted by display_order ascending.
 */
export function useTierChannels(tierId: string | null) {
  return useQuery<CommunityChannelRow[], Error>({
    queryKey: channelMgmtKeys.tierChannels(tierId),
    queryFn: () => fetchTierChannels(tierId),
    staleTime: 30 * 1000,
  });
}

/**
 * Fetch ARCHIVED channels in a tier. Super-admin only (RLS bypass). Used by
 * the Archived tab in CommunitySettings → Channel Management.
 */
export function useArchivedTierChannels(tierId: string | null) {
  return useQuery<CommunityChannelRow[], Error>({
    queryKey: channelMgmtKeys.archivedChannels(tierId),
    queryFn: () => fetchArchivedTierChannels(tierId),
    staleTime: 30 * 1000,
  });
}

/**
 * Invalidate everything that renders the channel list — management tabs,
 * sidebar, channelsWithTiers. Called after any create/update/archive/unarchive
 * mutation so the UI reflects the change in every surface at once.
 */
function invalidateChannelListCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  tierId: string | null,
) {
  queryClient.invalidateQueries({ queryKey: channelMgmtKeys.tierChannels(tierId) });
  queryClient.invalidateQueries({ queryKey: channelMgmtKeys.archivedChannels(tierId) });
  queryClient.invalidateQueries({ queryKey: communityKeys.channelsWithTiers });
  queryClient.invalidateQueries({ queryKey: communityKeys.channels() });
}

export function useCreateChannel() {
  const queryClient = useQueryClient();
  return useMutation<string, Error, ChannelCreateInput>({
    mutationFn: (input) => createChannel(input),
    onSuccess: (_newId, input) => {
      invalidateChannelListCaches(queryClient, input.tierId);
    },
  });
}

export function useUpdateChannel() {
  const queryClient = useQueryClient();
  // Input carries `tierId` alongside `ChannelUpdateInput` so we can invalidate
  // the right bucket. Caller supplies it from the row being edited.
  return useMutation<void, Error, ChannelUpdateInput & { tierId: string | null }>({
    mutationFn: (input) => updateChannel(input),
    onSuccess: (_v, input) => {
      invalidateChannelListCaches(queryClient, input.tierId);
    },
  });
}

export function useArchiveChannel() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { channelId: string; tierId: string | null }>({
    mutationFn: ({ channelId }) => archiveChannel(channelId),
    onSuccess: (_v, { tierId }) => {
      invalidateChannelListCaches(queryClient, tierId);
    },
  });
}

export function useUnarchiveChannel() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { channelId: string; tierId: string | null }>({
    mutationFn: ({ channelId }) => unarchiveChannel(channelId),
    onSuccess: (_v, { tierId }) => {
      invalidateChannelListCaches(queryClient, tierId);
    },
  });
}

// ===========================================
// Quick Links Management — CRUD + archive
// ===========================================

import {
  fetchScopedQuickLinks,
  fetchArchivedScopedQuickLinks,
  fetchQuickLinksWithTiers,
  createQuickLink,
  updateQuickLink,
  archiveQuickLink,
  unarchiveQuickLink,
  type CommunityQuickLinkRow,
  type QuickLinkCreateInput,
  type QuickLinkUpdateInput,
  type QuickLinkScope,
} from './community';

/** The single id backing a scope; null when neither side is set. */
function quickLinkScopeId(scope: QuickLinkScope | null | undefined): string | null {
  if (!scope) return null;
  return scope.communityId ?? scope.tierId ?? null;
}

const quickLinkMgmtKeys = {
  scoped: (scope: QuickLinkScope | null | undefined) =>
    [
      'community',
      'quickLinks',
      'active',
      scope?.communityId ? 'community' : 'tier',
      quickLinkScopeId(scope) ?? '',
    ] as const,
  archived: (scope: QuickLinkScope | null | undefined) =>
    [
      'community',
      'quickLinks',
      'archived',
      scope?.communityId ? 'community' : 'tier',
      quickLinkScopeId(scope) ?? '',
    ] as const,
  withTiers: ['community', 'quickLinksWithTiers'] as const,
};

/** Fetch ACTIVE quick links for a scope (tier or community). */
export function useScopedQuickLinks(scope: QuickLinkScope | null) {
  return useQuery<CommunityQuickLinkRow[], Error>({
    queryKey: quickLinkMgmtKeys.scoped(scope),
    queryFn: () => fetchScopedQuickLinks(scope as QuickLinkScope),
    enabled: !!quickLinkScopeId(scope),
    staleTime: 30 * 1000,
  });
}

/**
 * Fetch ARCHIVED quick links for a scope. RLS returns rows only to callers
 * who may manage the scope (super admins / community owners+admins).
 */
export function useArchivedScopedQuickLinks(scope: QuickLinkScope | null) {
  return useQuery<CommunityQuickLinkRow[], Error>({
    queryKey: quickLinkMgmtKeys.archived(scope),
    queryFn: () => fetchArchivedScopedQuickLinks(scope as QuickLinkScope),
    enabled: !!quickLinkScopeId(scope),
    staleTime: 30 * 1000,
  });
}

/** Bulk fetch of active quick links (used by the sidebar). */
export function useQuickLinksWithTiers() {
  return useQuery<CommunityQuickLinkRow[], Error>({
    queryKey: quickLinkMgmtKeys.withTiers,
    queryFn: () => fetchQuickLinksWithTiers(),
    staleTime: 30 * 1000,
  });
}

/**
 * Invalidate every surface that renders quick links — management tabs and
 * the sidebar — for the given scope.
 */
function invalidateQuickLinkListCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  scope: QuickLinkScope,
) {
  queryClient.invalidateQueries({ queryKey: quickLinkMgmtKeys.scoped(scope) });
  queryClient.invalidateQueries({ queryKey: quickLinkMgmtKeys.archived(scope) });
  queryClient.invalidateQueries({ queryKey: quickLinkMgmtKeys.withTiers });
}

export function useCreateQuickLink() {
  const queryClient = useQueryClient();
  return useMutation<string, Error, QuickLinkCreateInput>({
    mutationFn: (input) => createQuickLink(input),
    onSuccess: (_id, input) => {
      invalidateQuickLinkListCaches(queryClient, {
        tierId: input.tierId,
        communityId: input.communityId,
      });
    },
  });
}

export function useUpdateQuickLink() {
  const queryClient = useQueryClient();
  // Caller passes the row's scope alongside the update input so we can
  // invalidate the right bucket. The RPC itself does not touch scope.
  return useMutation<void, Error, QuickLinkUpdateInput & QuickLinkScope>({
    mutationFn: (input) => updateQuickLink(input),
    onSuccess: (_v, input) => {
      invalidateQuickLinkListCaches(queryClient, {
        tierId: input.tierId,
        communityId: input.communityId,
      });
    },
  });
}

export function useArchiveQuickLink() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { id: string } & QuickLinkScope>({
    mutationFn: ({ id }) => archiveQuickLink(id),
    onSuccess: (_v, { tierId, communityId }) => {
      invalidateQuickLinkListCaches(queryClient, { tierId, communityId });
    },
  });
}

export function useUnarchiveQuickLink() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { id: string } & QuickLinkScope>({
    mutationFn: ({ id }) => unarchiveQuickLink(id),
    onSuccess: (_v, { tierId, communityId }) => {
      invalidateQuickLinkListCaches(queryClient, { tierId, communityId });
    },
  });
}

// ===========================================
// Mentions — search + admin gate hooks
// ===========================================

import {
  searchMentionables,
  sharePostToConversations,
  type MentionablesResult,
} from './community';
import { supabase } from '@/lib/supabase';

/**
 * Mention-suggestion search (debounced by the caller — useMentionTrigger
 * hands us a query that trails typing by ~150ms). Empty query is allowed
 * (returns top-N). 30-second staleTime — autocomplete results rarely change
 * mid-session. Previous results stay visible while the next key's fetch is
 * in flight so the dropdown doesn't blink between keystrokes.
 */
export function useMentionablesSearch(opts: {
  query: string;
  tierId?: string | null;
  enabled?: boolean;
}) {
  const { query, tierId, enabled = true } = opts;
  return useQuery<MentionablesResult>({
    queryKey: ['community', 'mentionables', tierId ?? null, query.trim()],
    queryFn: () => searchMentionables({ query, tierId, limit: 8 }),
    enabled,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

/**
 * Returns true when the current user can mention @everyone in the given
 * scope. Mirrors the server-side rule in fn_strip_unauthorised_everyone
 * (migration 249): global admins always pass, otherwise the caller must be
 * an active community-direct member of the tier they're posting in.
 *
 * Pass `tierId` when posting inside a tier-scoped surface (community feed
 * channel, tier chat). Without a tier scope, only global admins qualify.
 */
export function useCanMentionEveryone(tierId?: string | null) {
  const { data: user } = useCurrentUser();
  return useQuery<boolean>({
    queryKey: ['community', 'canMentionEveryone', user?.id, tierId ?? null],
    enabled: !!user?.id,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      if (!user?.id) return false;

      // Global admin bypass — works in every scope.
      const { data: roleRow } = await supabase
        .from('global_user_roles')
        .select('role')
        .eq('user_id', user.id)
        .maybeSingle();
      const role = (roleRow as { role?: string } | null)?.role;
      if (role === 'admin' || role === 'super_admin') return true;

      // Tier-scoped: must be an active community-direct member of that tier.
      if (!tierId) return false;
      const { data: membership } = await supabase
        .from('user_community_memberships')
        .select('user_id')
        .eq('user_id', user.id)
        .eq('community_tier_id', tierId)
        .eq('status', 'active')
        .is('organization_id', null)
        .maybeSingle();
      return !!membership;
    },
  });
}

/**
 * Share a post into one or more conversations. Each insert fires the
 * trg_message_post_shared_outbox trigger which notifies the post author.
 */
export function useSharePostToConversations() {
  const queryClient = useQueryClient();
  return useMutation<
    void,
    Error,
    { postId: string; conversationIds: string[]; body?: string }
  >({
    mutationFn: (input) => sharePostToConversations(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community', 'conversations'] });
    },
  });
}

// ===========================================
// Custom Communities (migrations 378-383)
// ===========================================

export const customCommunityKeys = {
  hub: ['community', 'customCommunities', 'hub'] as const,
  adminList: (archived: boolean) =>
    ['community', 'customCommunities', archived ? 'archived' : 'active'] as const,
  members: (communityId: string) =>
    ['community', 'communityMembers', communityId] as const,
  tierGrants: (communityId: string) =>
    ['community', 'communityTierGrants', communityId] as const,
};

/** The custom communities the caller belongs to (all, for super admins). */
export function useCustomCommunitiesForHub() {
  return useQuery<CustomCommunityCard[], Error>({
    queryKey: customCommunityKeys.hub,
    queryFn: fetchCustomCommunitiesForHub,
    staleTime: 30_000,
    gcTime: 10 * 60_000,
  });
}

/**
 * Unified switcher cards: tier communities (ladder/code-filtered inside
 * fetchCommunityTiersForHub — that filter must NEVER apply to custom
 * communities) + the caller's custom communities.
 */
export function useHubCards(): {
  tiers: HubCard[];
  communities: HubCard[];
  all: HubCard[];
  isLoading: boolean;
} {
  const tiersQuery = useCommunityTiersForHub();
  const communitiesQuery = useCustomCommunitiesForHub();

  const tiers = useMemo<HubCard[]>(
    () => (tiersQuery.data ?? []).map((t) => ({ ...t, kind: 'tier' as const })),
    [tiersQuery.data],
  );
  const communities = useMemo<HubCard[]>(
    () => communitiesQuery.data ?? [],
    [communitiesQuery.data],
  );
  const all = useMemo<HubCard[]>(
    () => [...tiers, ...communities],
    [tiers, communities],
  );

  return {
    tiers,
    communities,
    all,
    isLoading: tiersQuery.isLoading || communitiesQuery.isLoading,
  };
}

/** Admin list (active or archived) — super-admin surface. */
export function useCustomCommunities(opts: { archived?: boolean } = {}) {
  const archived = opts.archived ?? false;
  return useQuery<CustomCommunityRow[], Error>({
    queryKey: customCommunityKeys.adminList(archived),
    queryFn: () => fetchCustomCommunities({ archived }),
    staleTime: 30_000,
  });
}

function invalidateCommunityCaches(queryClient: QueryClient, communityId?: string) {
  queryClient.invalidateQueries({ queryKey: ['community', 'customCommunities'] });
  if (communityId) {
    queryClient.invalidateQueries({ queryKey: customCommunityKeys.members(communityId) });
    queryClient.invalidateQueries({ queryKey: customCommunityKeys.tierGrants(communityId) });
  }
  // The community chat is a conversation — membership changes alter the list.
  queryClient.invalidateQueries({ queryKey: communityKeys.conversations() });
}

export function useCreateCustomCommunity() {
  const queryClient = useQueryClient();
  return useMutation<string, Error, CustomCommunityCreateInput>({
    mutationFn: (input) => createCustomCommunity(input),
    onSuccess: () => invalidateCommunityCaches(queryClient),
  });
}

export function useUpdateCustomCommunity() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, CustomCommunityUpdateInput>({
    mutationFn: (input) => updateCustomCommunity(input),
    onSuccess: (_data, { communityId }) =>
      invalidateCommunityCaches(queryClient, communityId),
  });
}

export function useArchiveCustomCommunity() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { communityId: string }>({
    mutationFn: ({ communityId }) => archiveCustomCommunity(communityId),
    onSuccess: (_data, { communityId }) =>
      invalidateCommunityCaches(queryClient, communityId),
  });
}

export function useUnarchiveCustomCommunity() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { communityId: string }>({
    mutationFn: ({ communityId }) => unarchiveCustomCommunity(communityId),
    onSuccess: (_data, { communityId }) =>
      invalidateCommunityCaches(queryClient, communityId),
  });
}

export function useCommunityTierGrants(communityId: string | undefined) {
  return useQuery<CommunityTierGrant[], Error>({
    queryKey: customCommunityKeys.tierGrants(communityId ?? 'none'),
    queryFn: () => fetchCommunityTierGrants(communityId as string),
    enabled: !!communityId,
    staleTime: 30_000,
  });
}

export function useGrantCommunityTierAccess() {
  const queryClient = useQueryClient();
  return useMutation<number, Error, { communityId: string; tierId: string; autoProvision?: boolean }>({
    mutationFn: ({ communityId, tierId, autoProvision }) =>
      grantCommunityTierAccess(communityId, tierId, autoProvision ?? true),
    onSuccess: (_count, { communityId }) =>
      invalidateCommunityCaches(queryClient, communityId),
  });
}

export function useRevokeCommunityTierAccess() {
  const queryClient = useQueryClient();
  return useMutation<number, Error, { communityId: string; tierId: string }>({
    mutationFn: ({ communityId, tierId }) =>
      revokeCommunityTierAccess(communityId, tierId),
    onSuccess: (_count, { communityId }) =>
      invalidateCommunityCaches(queryClient, communityId),
  });
}

export function useCommunityMembers(communityId: string | undefined) {
  return useQuery<CommunityMemberRow[], Error>({
    queryKey: customCommunityKeys.members(communityId ?? 'none'),
    queryFn: () => fetchCommunityMembers(communityId as string),
    enabled: !!communityId,
    staleTime: 10_000,
  });
}

export function useAddCommunityMembers() {
  const queryClient = useQueryClient();
  return useMutation<number, Error, { communityId: string; userIds: string[] }>({
    mutationFn: ({ communityId, userIds }) => addCommunityMembers(communityId, userIds),
    onSuccess: (_count, { communityId }) =>
      invalidateCommunityCaches(queryClient, communityId),
  });
}

export function useRemoveCommunityMember() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { communityId: string; userId: string }>({
    mutationFn: ({ communityId, userId }) => removeCommunityMember(communityId, userId),
    onSuccess: (_data, { communityId }) =>
      invalidateCommunityCaches(queryClient, communityId),
  });
}

export function useUpdateCommunityMemberRole() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { communityId: string; userId: string; role: 'owner' | 'admin' | 'member' }>({
    mutationFn: ({ communityId, userId, role }) =>
      updateCommunityMemberRole(communityId, userId, role),
    onSuccess: (_data, { communityId }) =>
      invalidateCommunityCaches(queryClient, communityId),
  });
}
